import type { CardLoadout } from '@kenocyber/shared';
import {
  encodeHealthMilli,
  type JewelSocketProfile,
  PVP_DUEL_PROTOCOL_VERSION,
  PVP_MAX_COMMAND_BYTES,
  PVP_MAX_COMMANDS_PER_SECOND,
  PVP_MAX_SEQUENCE_SKIP,
  PVP_RATE_LIMIT_CLOSE_THRESHOLD,
  PVP_RULES_VERSION,
  PVP_TEAM_PROTOCOL_VERSION,
  PVP_TEAMS,
  type PvpAppearance,
  type PvpCloseReason,
  type PvpCombatProfileWire,
  type PvpMatchState,
  type PvpParticipantId,
  type PvpParticipantRef,
  type PvpPlayableMode,
  type PvpResult,
  type PvpRuleset,
  type PvpSeat,
  type PvpServerFrame,
  type PvpTeam,
  type PvpTeamAbandonmentFrame,
  type PvpTeamSelf,
  type PvpTeamServerFrame,
  type PvpTeamSlot,
  pvpClientFrameSchema,
  pvpNickname,
  pvpReadAppearance,
  type SimulationSeed,
} from '@kenocyber/shared';
import type { HostilePayloadKind } from '../damage/reflection';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_MAX_CATCHUP_MS,
  PVP_MAX_HEALTH_MILLI,
  PVP_OBSTACLES,
  PVP_PROJECTILE_RADIUS_MU,
  PVP_RECONNECT_GRACE_MS,
  PVP_SHIP_RADIUS_MU,
  PVP_TICK_MS,
  PVP_TIME_LIMIT_TICKS,
} from './balance';
import {
  PVP_CARD_SLOT_BY_NUMBER,
  pvpCardBlastFields,
  pvpCardFlagFields,
  pvpCardSlotsOf,
  pvpCardVisualFields,
  pvpSummonFrameOf,
} from './card-frames';
import { pvpCombatProfileToWire } from './combat-profile';
import {
  PVP_DUEL_RULESET,
  type PvpSnapshot,
  pvpDuelSeatToParticipantId,
  pvpDuelSeatToTeam,
  pvpParticipantIdToDuelSeat,
  pvpRequireDuelRuleset,
  pvpTeamResultToDuelResult,
} from './legacy-duel';
import {
  PVP_ROOM_STATE_V3_VERSION,
  type PvpRoomChairV3,
  type PvpRoomSimulationV3,
  type PvpRoomStateV3,
  pvpReadRoomState,
} from './room-state';
import {
  type PvpAbandonment,
  type PvpParticipantSeat,
  type PvpTeamResult,
  pvpCanonicalSeats,
} from './roster';
import { PvpSimulation } from './simulation';
import { shipHasSuperCannons } from './super-cannons';
import {
  pvpSawFrameFields,
  pvpTeamRosterOf,
  pvpTeamSelfOf,
  pvpTeamStateFrameOf,
  pvpTeamWelcomeOf,
  pvpTeamWorldOf,
} from './team-wire';

/**
 * The room, with no transport in it, for a match of any shape.
 *
 * ## Three classes, three jobs, and the line between them
 *
 * - **`PvpSimulation`** is COMBAT. Where a hull is, what a shot takes off, who
 *   is alive, which pad grew what. It takes no clock, holds no identity beyond
 *   a participant id, and can be re-run from a snapshot by a process that has
 *   never heard of an account.
 * - **`PvpRoom`** - this file - is the MATCH. Who holds each chair, which
 *   generation of their connection is current, who has declared ready, when the
 *   first tick may run, what a dropped socket costs, who is a spectator, and
 *   what has already been announced. It decides every rule and owns no socket.
 * - **`PvpRoomObject`** is TRANSPORT. Sockets, Durable Object storage, alarms,
 *   tickets, hibernation. It decides nothing at all: it hands the room a clock
 *   reading and sends what the room asks to be sent.
 *
 * Nothing about teams lives in the Durable Object, and nothing about sockets
 * lives here. That split is what lets abandonment, replacement, six-way
 * readiness, two tabs, replayed commands and rate limiting all be proven in
 * plain Vitest - and it is why a chair can never be reached except through the
 * identity somebody was assigned.
 *
 * ## Identity, never position
 *
 * Everything the room keys is keyed by {@link PvpParticipantId}. Not by seat -
 * which only exists for the two-seat wire - and above all not by array index: a
 * roster is a list, a list reorders, and the first time one arrives in another
 * order every piece of state inherits somebody else's. `a2` is `a2` in the
 * lobby, on the socket, in the snapshot, after a reconnection, in the result and
 * in the audit trail, and there is no arrangement of arrivals that makes it
 * anything else.
 *
 * ## Time arrives, it is never asked for
 *
 * There is no `Date.now()` in this file. Every method takes `now`, so a test can
 * advance twelve seconds without waiting twelve seconds, and so the room cannot
 * read a clock twice inside one tick and get two answers for the same instant.
 *
 * ## What a duel still is
 *
 * A `duel_1v1` is a two-participant room - `a1` and `b1` - and every number,
 * frame, transition and stored byte it produces is what it produced before this
 * class knew what a team was. The seat-shaped methods at the bottom are
 * ADAPTERS: they convert at the boundary and refuse rather than lie when the
 * match has no seats. `a2` never becomes seat `a`.
 */

/* -------------------------------------------------------------------------- */
/*  Refusals                                                                  */
/* -------------------------------------------------------------------------- */

/** Every way the room says no to a caller. One code per rule. */
export type PvpRoomRefusal =
  /** No such chair in this ruleset. A 1x1 has no `a2`, and never will. */
  | 'unknown_participant'
  /** The chair belongs to somebody else, and this is not them. */
  | 'occupant_mismatch'
  /** This account already holds another chair in this room. */
  | 'account_already_seated'
  /** A chair cannot change hands once the first tick has run. */
  | 'replacement_after_start'
  /** The match is over. Nothing may be claimed, replaced or attached. */
  | 'room_is_over';

export class PvpRoomError extends RangeError {
  override readonly name = 'PvpRoomError';

  constructor(
    readonly code: PvpRoomRefusal,
    message: string,
  ) {
    super(message);
  }
}

/* -------------------------------------------------------------------------- */
/*  What a step carries                                                       */
/* -------------------------------------------------------------------------- */

/**
 * One live connection.
 *
 * Keyed by the id the room minted, and carrying the GENERATION it was opened
 * under. The generation is what makes "the tab that came back" and "the tab
 * that went away" two different things: a message from a superseded connection
 * is not this participant speaking, however valid its contents.
 *
 * Readiness is NOT here. It belongs to the chair, because a person who declared
 * themselves ready and then reloaded is a person the room must ask again - and
 * a flag that died with the socket could not express the difference.
 */
export interface PvpConnection {
  readonly connectionId: string;
  readonly participantId: PvpParticipantId;
  readonly generation: number;
  /** Commands seen inside the current one-second window. */
  windowStartedAt: number;
  windowCount: number;
  /** Commands refused for rate, size or shape. Sustained abuse closes the socket. */
  dropped: number;
}

/**
 * What a frame may be: the duel wire, or the team one.
 *
 * A union rather than a widened single protocol. A client speaks one of the
 * two, decided by the capability it presented - never by inspecting a frame -
 * and the two schemas stay strict because neither had to grow optional fields
 * to accommodate the other.
 */
export type PvpOutboundPayload = PvpServerFrame | PvpTeamServerFrame;

export interface PvpOutboundFrame {
  readonly connectionId: string;
  readonly frame: PvpOutboundPayload;
}

export interface PvpCloseInstruction {
  readonly connectionId: string;
  readonly reason: PvpCloseReason;
}

/**
 * What the room decided, in its own vocabulary.
 *
 * ## Why these are not frames
 *
 * A frame is addressed to a socket and shaped by a wire; an announcement is a
 * FACT about the match. The two came apart the moment a room could hold six
 * people: "b2's hull is gone" is true whether or not anybody is listening,
 * whether or not a wire exists to say it in, and it must happen exactly once
 * however many times the room is pumped, rebuilt or asked for its result.
 *
 * So the room states the fact and the transport decides what to do with it.
 * That is what makes "each critical transition occurs once" a property a test
 * can assert directly rather than infer from counting packets.
 */
export type PvpRoomAnnouncement =
  /** The first tick is about to run. Once per room, and never per tick. */
  | { readonly kind: 'room_started' }
  /** This participant's reconnect grace expired. They chose not to come back. */
  | {
      readonly kind: 'participant_abandoned';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
    }
  /** This participant's hull is gone, and why. Never twice for the same hull. */
  | {
      readonly kind: 'participant_eliminated';
      readonly participantId: PvpParticipantId;
      readonly team: PvpTeam;
      readonly cause: 'combat' | 'abandonment';
    }
  | { readonly kind: 'team_won'; readonly team: PvpTeam }
  /** Nobody won and that is a real answer: everybody fell, or everybody left. */
  | { readonly kind: 'draw' }
  /** The clock ran out. A draw by the rules, never a win on points. */
  | { readonly kind: 'timeout' }
  /** The room could not vouch for itself. There is no trustworthy winner. */
  | { readonly kind: 'service_failure' }
  /** The match is over, carrying the one result everything else derives from. */
  | { readonly kind: 'room_ended'; readonly result: PvpTeamResult };

export interface PvpRoomStep {
  readonly frames: readonly PvpOutboundFrame[];
  readonly closes: readonly PvpCloseInstruction[];
  /**
   * Non-null exactly once: the pump the match became terminal.
   *
   * A TEAM result, which is the internal truth. A duel's seat-shaped answer is
   * derived from it by `pvpTeamResultToDuelResult`, at the boundary that speaks
   * seats, and nowhere else - a `winner_seat` written for a team match would
   * name a person who does not exist.
   */
  readonly finished: PvpTeamResult | null;
  readonly announcements: readonly PvpRoomAnnouncement[];
}

export interface PvpRoomState {
  readonly matchId: string;
  readonly mode: PvpPlayableMode;
  readonly state: PvpMatchState;
  readonly snapshot: PvpSnapshot;
}

/**
 * Um proc a caminho de uma publicacao, ainda no vocabulario da SIMULACAO.
 *
 * Enderecado por participante porque e assim que a simulacao fala. A traducao
 * para o vocabulario de cada protocolo - assento no duelo, participante no 3x3
 * - acontece na difusao, que e o unico sitio que sabe qual dos dois esta a
 * escrever.
 */
interface PvpProcOutbox {
  readonly i: number;
  readonly e: string;
  readonly p: PvpParticipantId;
  readonly q: PvpParticipantId;
  /**
   * O tique da SIMULAÇÃO em que o evento nasceu (gate V28G.I).
   *
   * Copiado do evento e nunca recalculado aqui. A caixa de saída existe
   * precisamente porque uma publicação junta vários tiques: substituí-lo pelo
   * tique da publicação apagaria a distinção que ela foi criada para preservar.
   */
  readonly t: number;
  /** A vida realmente acrescentada, em milli. Ausente para o que não cura. */
  readonly h?: bigint;
  readonly x: number;
  readonly y: number;
}

/**
 * Uma explosão à espera da próxima publicação (gate V28G.J).
 *
 * A gémea exata de {@link PvpProcOutbox}, e existe pela mesma razão: a sala
 * simula a 20 Hz e publica à cadência de envio do cliente, portanto duas bombas
 * podem detonar entre duas publicações e guardar só a última perderia a outra em
 * silêncio.
 *
 * A tradução do endereçamento - assento no duelo, participante no 3x3 -
 * acontece na difusão, que é o único sítio que sabe qual dos dois está a
 * escrever.
 */
interface PvpBlastOutbox {
  readonly i: number;
  readonly e: string;
  readonly p: PvpParticipantId;
  /** O tique da SIMULAÇÃO em que ela detonou. Nunca o da publicação. */
  readonly t: number;
  readonly x: number;
  readonly y: number;
  /** O raio MECÂNICO, copiado do evento e nunca recalculado aqui. */
  readonly r: number;
  /** Quem levou dano. Nunca quem estava por perto. */
  readonly q: readonly PvpParticipantId[];
}

/**
 * Uma DETONAÇÃO de sangramento à espera da próxima publicação (gate V28G.N).
 *
 * A terceira caixa de saída, e existe pela mesma razão que as duas de cima: a
 * sala simula a 20 Hz e publica à cadência de envio do cliente, portanto dois
 * jogadores podem detonar entre duas publicações e guardar só a última perderia
 * a outra em silêncio.
 *
 * A tradução do endereçamento - assento no duelo, participante no 3x3 - acontece
 * na difusão, que é o único sítio que sabe qual dos dois está a escrever.
 */
interface PvpBleedOutbox {
  /** Quem mandou. Metade da chave de deduplicação. */
  readonly p: PvpParticipantId;
  /** O tique da SIMULAÇÃO em que a ordem foi executada. A outra metade. */
  readonly t: number;
  /** Um alvo detonado, e quantas marcas rebentaram nele. */
  readonly q: readonly {
    readonly s: PvpParticipantId;
    readonly n: number;
    /** O dano realmente aplicado, em pontos inteiros. */
    readonly d: bigint;
  }[];
}

/**
 * Uma REFLEXÃO à espera de publicação (gate V28G.O).
 *
 * A gémea das três de cima, e existe pela mesma razão exata: a sala simula a
 * 20 Hz e publica mais devagar, portanto duas interceções podem acontecer entre
 * duas publicações e guardar só a última perderia a outra em silêncio.
 *
 * A tradução do endereçamento - assento no duelo, participante no 3x3 - acontece
 * na difusão, que é o único sítio que sabe qual dos dois está a escrever.
 */
interface PvpReflectOutbox {
  /** O EFEITO confirmado, nunca a joia. */
  readonly e: string;
  /** O REFLETOR, e o novo autor do payload. */
  readonly p: PvpParticipantId;
  /** Quem recebe o payload redirecionado. */
  readonly q: PvpParticipantId;
  /** O tique da SIMULAÇÃO. Nunca o de publicação. */
  readonly t: number;
  /** Que instância hostil foi intercetada. Um tipo, nunca uma joia. */
  readonly k: string;
  /** `i` acende o brilho; `l` imprime o número. */
  readonly s: 'i' | 'l';
  readonly x: number;
  readonly y: number;
  readonly tx: number;
  readonly ty: number;
  /** O dano realmente aplicado, em MILLI, presente só em `l`. */
  readonly d?: bigint;
}

/** A chair as anybody outside the room may read it. Deliberately no account. */
export interface PvpChairView extends PvpParticipantSeat {
  readonly claimed: boolean;
  readonly generation: number;
  readonly connected: boolean;
  readonly ready: boolean;
  readonly disconnectedSince: number | null;
  readonly appearance: PvpAppearance | null;
}

/* -------------------------------------------------------------------------- */
/*  The chair                                                                 */
/* -------------------------------------------------------------------------- */

/** The room's own mutable record of one chair. Never handed out. */
interface Chair {
  readonly participantId: PvpParticipantId;
  readonly team: PvpTeam;
  readonly slot: PvpTeamSlot;
  claimed: boolean;
  accountId: string | null;
  /**
   * O nome público de quem se sentou, ou `null` (gate V28H.R).
   *
   * Chega pelo BILHETE assinado, como a aparência, e nunca pelo socket: um
   * cliente que pudesse dizer como se chama podia dizer que se chama outra
   * pessoa. Vive na cadeira e não na simulação porque não é regra - ele não
   * decide nada, só identifica.
   */
  nickname: string | null;
  generation: number;
  connectionId: string | null;
  ready: boolean;
  disconnectedSince: number | null;
  /**
   * When this player CHOSE to abandon, or null (ADR 0043).
   *
   * Deliberately not `disconnectedSince`. That field means "the socket went and
   * the chair is being held", and holding it is the whole point of the
   * reconnection grace. This one means "the player pressed the button", which
   * ends no differently from an expired grace but must not wait for one.
   *
   * Keeping them apart is also what keeps the duel's tie-break honest: it
   * compares WHEN two people went, and a voluntary leaver may never have been
   * disconnected at all.
   */
  leftAt: number | null;
  appearance: PvpAppearance | null;
  /**
   * Se o CASCO desta cadeira já foi congelado (gate V28G.R).
   *
   * Uma pergunta diferente de "esta cadeira já tem dono", e a diferença é uma
   * substituição. `accountId` passa a estar preenchido no instante em que o
   * substituto é registado - antes de ele se ligar - por isso "primeira vez a
   * sentar-se" deixaria de ser verdade e o bilhete DELE nunca chegaria a
   * congelar nada. Este campo segue o casco: `replaceOccupant` repõe-o a
   * `false` no mesmo passo em que repõe a nave.
   */
  hullFrozen: boolean;
  abandonAnnounced: boolean;
  eliminationAnnounced: boolean;
}

/**
 * Why this chair counts as gone, and who it is.
 *
 * The one place the two abandonments are told apart, and it is a READ of the
 * chair rather than a flag anybody set: `leftAt` is written only by the frame
 * that asked to leave, so a chair carrying one chose to go and a chair without
 * one ran out of grace. Voluntary wins a chair that is somehow both - the
 * decision came first, and it is the truer thing to tell that player.
 */
function abandonmentOf(chair: Chair): PvpAbandonment {
  return {
    cause: chair.leftAt !== null ? 'voluntary' : 'reconnect_timeout',
    participantId: chair.participantId,
  };
}

const EMPTY_STEP: PvpRoomStep = { frames: [], closes: [], finished: null, announcements: [] };

function emptyStep(): PvpRoomStep {
  return EMPTY_STEP;
}

export interface PvpRoomOptions {
  /** Which shape of match this is. A room without one is the shipped duel. */
  readonly ruleset?: PvpRuleset;
  readonly state?: PvpMatchState;
  /** The historic duel snapshot. Only ever a `duel_1v1`. */
  readonly snapshot?: PvpSnapshot;
  /** A tagged simulation, as a persisted room carries it. Used by `deserialize`. */
  readonly simulation?: PvpRoomSimulationV3;
  /**
   * Who sits where, when the caller already knows.
   *
   * A lobby fills every chair before a room exists and hands the whole roster
   * over; the shipped duel does not, because its seats are claimed one socket
   * at a time as the tickets are presented. Both are supported and neither is a
   * default: a chair whose occupant is unknown is `claimed: false` and refuses
   * to start a match, which is exactly what it is.
   *
   * The ORDER is ignored - a roster is canonicalised by team then slot - so a
   * lobby that filled its slots out of order produces the same room.
   */
  readonly roster?: readonly PvpParticipantRef[];
  /** What each participant looks like, when the caller already knows. */
  readonly appearance?: ReadonlyArray<readonly [PvpParticipantId, PvpAppearance]>;
  /**
   * The generator's seed, for a NEW room.
   *
   * Passed in, never derived here, and never optional in effect: a room with
   * neither a seed nor a simulation throws. The old version derived it from the
   * match id, which is public - see `pvpNewPrivateSeed` for what that let
   * anybody holding the id do.
   */
  readonly seed?: SimulationSeed;
}

export class PvpRoom {
  private readonly simulation: PvpSimulation;
  readonly ruleset: PvpRuleset;
  private matchState: PvpMatchState;
  private readonly connections = new Map<string, PvpConnection>();
  /** Every chair the ruleset has, canonical order, keyed by identity. */
  private readonly chairs: ReadonlyMap<PvpParticipantId, Chair>;
  private readonly orderedChairs: readonly Chair[];
  private accumulatorMs = 0;
  /** When a frame last went out. Presentation cadence, not simulated time. */
  private lastBroadcastAt = 0;
  private lastPumpAt: number | null = null;
  private startAnnounced = false;
  private announced = false;

  constructor(
    readonly matchId: string,
    readonly mode: PvpPlayableMode,
    options: PvpRoomOptions = {},
  ) {
    this.ruleset = options.ruleset ?? PVP_DUEL_RULESET;
    /*
     * A restored room takes the generator's live state from its snapshot, so a
     * rebuild after an eviction continues the SAME sequence rather than
     * starting a new one - which is the property a hibernating match depends on.
     */
    if (options.simulation) {
      /*
       * Three kinds, one of which is only ever read. `duel_v2` goes through the
       * historic decoder, which builds the shipped profile and converts a
       * cooldown from whole ticks into milli-ticks; the next `serialize()`
       * writes `duel_v3` and the record never goes back.
       */
      this.simulation =
        options.simulation.kind === 'duel_v2'
          ? PvpSimulation.restore(options.simulation.snapshot)
          : PvpSimulation.restoreV3(options.simulation.snapshot);
    } else if (options.snapshot) {
      this.simulation = PvpSimulation.restore(options.snapshot);
    } else if (options.seed !== undefined) {
      this.simulation = PvpSimulation.create(options.seed, this.ruleset);
    } else {
      /*
       * Fail closed.
       *
       * A default here would be a default somebody relies on, and the only
       * defaults available are guessable ones - the match id, a constant, a
       * clock. Refusing costs one line at each call site and makes an
       * accidentally public sequence impossible to write.
       */
      throw new RangeError('a new pvp room needs a seed; see pvpNewPrivateSeed');
    }
    if (this.simulation.matchRuleset !== this.ruleset) {
      throw new RangeError(
        `a ${this.ruleset} room cannot hold a ${this.simulation.matchRuleset} simulation`,
      );
    }

    const chairs = new Map<PvpParticipantId, Chair>();
    const ordered: Chair[] = [];
    for (const seat of pvpCanonicalSeats(this.ruleset)) {
      const chair: Chair = {
        participantId: seat.participantId,
        team: seat.team,
        slot: seat.slot,
        claimed: false,
        accountId: null,
        generation: 0,
        connectionId: null,
        ready: false,
        disconnectedSince: null,
        leftAt: null,
        appearance: null,
        nickname: null,
        hullFrozen: false,
        abandonAnnounced: false,
        eliminationAnnounced: false,
      };
      chairs.set(chair.participantId, chair);
      ordered.push(chair);
    }
    this.chairs = chairs;
    this.orderedChairs = ordered;
    this.matchState = options.state ?? 'proposed';

    for (const ref of options.roster ?? []) {
      this.claim(ref.participantId, ref.accountId);
    }
    for (const [participantId, profile] of options.appearance ?? []) {
      this.requireChair(participantId).appearance = profile;
    }
  }

  /* ------------------------------------------------------------------------ */
  /*  Reading the room                                                        */
  /* ------------------------------------------------------------------------ */

  get state(): PvpMatchState {
    return this.matchState;
  }

  /** How the match ended, by TEAM. The internal truth every answer derives from. */
  get teamResult(): PvpTeamResult | null {
    return this.simulation.teamResult;
  }

  /**
   * **DEV/E2E ONLY** — arma as próximas `count` salvas de cada cadeira com uma
   * bomba, se o casco tiver a joia.
   *
   * Um encaminhamento e mais nada: a decisão, a disciplina e as garantias vivem
   * em {@link PvpSimulation.devForceVolleyBombs}. Existe aqui porque o objeto
   * que hospeda a sala não tem outra forma de lhe falar - e não devia ter: o
   * `fetch` dele recusa tudo o que não é um upgrade de websocket, de propósito,
   * "a route that could read or nudge a live match is a route somebody
   * eventually points at a real one".
   *
   * Quem o chama é o objeto da sala, uma vez, ao construí-la, e só quando o
   * ambiente o autoriza. Em produção a variável nunca é definida e este método
   * nunca é chamado.
   */
  devForceVolleyBombs(count: number): void {
    this.simulation.devForceVolleyBombs(count);
  }

  /**
   * **DEV/E2E ONLY** — arma as próximas `count` salvas de cada cadeira com um
   * míssil, se o casco tiver a joia (gate V28G.L).
   *
   * Um encaminhamento e mais nada, gémeo do de cima: a decisão, a disciplina e as
   * garantias vivem em {@link PvpSimulation.devForceVolleyMissiles}. Existe aqui
   * pela mesma razão - o objeto que hospeda a sala não tem outra forma de lhe
   * falar, e o `fetch` dele recusa tudo o que não é um upgrade de websocket.
   *
   * Quem o chama é o objeto da sala, uma vez, ao construí-la, e só quando o
   * ambiente o autoriza. Em produção a variável nunca é definida.
   */
  devForceVolleyMissiles(count: number): void {
    this.simulation.devForceVolleyMissiles(count);
  }

  /**
   * **DEV/E2E ONLY** — arma as próximas `count` salvas de cada cadeira com uma
   * cura, se o casco tiver a joia (gate V28G.U).
   *
   * Um encaminhamento e mais nada, gémeo dos dois de cima: a decisão, a
   * disciplina e as garantias vivem em {@link PvpSimulation.devForceVitalHeals}.
   * Existe aqui pela mesma razão - o objeto que hospeda a sala não tem outra
   * forma de lhe falar, e o `fetch` dele recusa tudo o que não é um upgrade de
   * websocket.
   *
   * Quem o chama é o objeto da sala, uma vez, ao construí-la, e só quando o
   * ambiente o autoriza. Em produção a variável nunca é definida.
   */
  devForceVitalHeals(count: number): void {
    this.simulation.devForceVitalHeals(count);
  }

  /**
   * DEV/E2E ONLY: arma os próximos acertos diretos com veneno (gate V28G.M).
   *
   * Encaminha e mais nada. As garantias vivem em
   * {@link PvpSimulation.devForcePoison}. Existe aqui porque o objeto da sala
   * não fala com a simulação diretamente, e em produção nada a chama - a
   * variável de ambiente que a autoriza nunca é definida.
   */
  devForcePoison(count: number): void {
    this.simulation.devForcePoison(count);
  }

  /**
   * DEV/E2E ONLY: arma os próximos acertos diretos com sangramento (V28G.N).
   *
   * Encaminha e mais nada. As garantias vivem em
   * {@link PvpSimulation.devForceBleeding}. Existe aqui pela mesma razão que a
   * irmã acima, e em produção nada a chama - a variável de ambiente que a
   * autoriza nunca é definida.
   */
  /**
   * DEV/E2E ONLY: arma as próximas interceções de cada cadeira (gate V28G.O).
   *
   * Encaminha e mais nada. As garantias vivem em
   * {@link PvpSimulation.devForceReflect}, e a mais importante é que ela **não
   * desliga a recusa da recursão**: um payload já refletido nem sequer chega à
   * comparação que ela substitui.
   */
  devForceReflect(count: number, kinds?: readonly HostilePayloadKind[]): void {
    this.simulation.devForceReflect(count, kinds);
  }

  /**
   * DEV/E2E ONLY: arma os próximos projéteis com um corte (gate V28G.O).
   *
   * Encaminha e mais nada. As garantias vivem em
   * {@link PvpSimulation.devForceCyclone}. Existe para a avaliação manual da
   * Reflexão poder mostrar um corte REDIRECIONADO sem esperar que duas chances
   * calhem no mesmo projétil.
   */
  devForceCyclone(count: number): void {
    this.simulation.devForceCyclone(count);
  }

  devForceBleeding(count: number): void {
    this.simulation.devForceBleeding(count);
  }

  /** Every chair, canonical order, without saying who is in any of them. */
  chairViews(): readonly PvpChairView[] {
    return this.orderedChairs.map((chair) => ({
      participantId: chair.participantId,
      team: chair.team,
      slot: chair.slot,
      claimed: chair.claimed,
      generation: chair.generation,
      connected: chair.connectionId !== null,
      ready: chair.ready,
      disconnectedSince: chair.disconnectedSince,
      appearance: chair.appearance,
    }));
  }

  hasParticipant(participantId: PvpParticipantId): boolean {
    return this.chairs.has(participantId);
  }

  /** Which generation of this chair's connection is current. Never rewound. */
  generationOf(participantId: PvpParticipantId): number {
    return this.requireChair(participantId).generation;
  }

  /**
   * Who holds this chair, when the room knows.
   *
   * `null` means NOT RECORDED, never "nobody" - {@link PvpRoom.chairIsClaimed}
   * is the question about emptiness. A room upgraded from a version 2 record
   * knows its chairs are held and cannot know by whom, because version 2 never
   * wrote an account down.
   */
  occupantOf(participantId: PvpParticipantId): string | null {
    return this.requireChair(participantId).accountId;
  }

  chairIsClaimed(participantId: PvpParticipantId): boolean {
    return this.requireChair(participantId).claimed;
  }

  /**
   * Whether this participant may still act, or only watch.
   *
   * A spectator is somebody whose hull is gone - to a shot or to an expired
   * grace window - in a match that has begun. They keep their connection, keep
   * receiving state, and are refused every command, which is the whole of what
   * "spectator" means here. The room does not choose a camera and does not know
   * what a spectator's screen looks like.
   */
  isSpectator(participantId: PvpParticipantId): boolean {
    if (this.matchState !== 'running' && this.matchState !== 'completed') {
      return false;
    }
    return this.simulation.shipOf(participantId).healthMilli <= 0n;
  }

  /**
   * Everyone entitled to receive the room's state right now.
   *
   * Spectators included, deliberately: a player whose ship is gone is still in
   * the match and still watching it, and a room that stopped addressing them
   * would be a room where dying looks like being disconnected. What a spectator
   * may not do is SEND, which is decided in {@link PvpRoom.message}.
   */
  audience(): readonly PvpParticipantId[] {
    return this.orderedChairs
      .filter((chair) => chair.connectionId !== null)
      .map((chair) => chair.participantId);
  }

  /* ------------------------------------------------------------------------ */
  /*  The roster                                                              */
  /* ------------------------------------------------------------------------ */

  /**
   * Records that a chair belongs to an account.
   *
   * Refuses a chair somebody else holds and an account that already holds
   * another chair in this room: one account in two chairs is one pair of hands
   * driving two ships, and two accounts in one chair is a chair handed out
   * twice. Re-claiming with the SAME account is a no-op, so a retry costs
   * nothing - which is what makes an accept flow safe to repeat.
   *
   * A chair recorded as held by an account the room does not KNOW - the version
   * 2 case, where the occupant lived on a socket and was never persisted -
   * adopts the account it is told about rather than refusing it. The caller has
   * already authenticated them; the record simply never wrote it down.
   */
  claim(participantId: PvpParticipantId, accountId: string): void {
    const chair = this.requireChair(participantId);
    if (chair.accountId !== null && chair.accountId !== accountId) {
      throw new PvpRoomError(
        'occupant_mismatch',
        `${participantId} belongs to somebody else in this room`,
      );
    }
    for (const other of this.orderedChairs) {
      if (other !== chair && other.accountId === accountId) {
        throw new PvpRoomError(
          'account_already_seated',
          `this account already holds ${other.participantId}`,
        );
      }
    }
    chair.accountId = accountId;
    chair.claimed = true;
  }

  /**
   * Hands a chair to somebody else, before a single tick has run.
   *
   * ## Why this is an operation and not "just claim again"
   *
   * Because a substitution has to REVOKE. The person leaving may still hold a
   * live socket, a ticket they were issued, and a callback in flight; if the
   * chair simply changed hands, every one of those would go on addressing `a1`
   * and the room would have no way to tell the old occupant from the new one.
   * Holding `a1` is not authority - holding `a1` AT THE CURRENT GENERATION,
   * as the account the room recorded, is.
   *
   * So the generation advances, the previous logical connection is invalidated
   * and closed, readiness goes back to false (a new person has not agreed to
   * anything), the grace window is cleared, and the appearance may be replaced
   * because it is a new ship. Everything the old occupant could present is
   * stale by construction rather than by a check somebody has to remember.
   *
   * ## And why only before `running`
   *
   * A chair that changed hands mid-match would hand somebody a hull with a
   * history: damage they did not take, a rune they did not collect, a position
   * they did not choose. The refusal after the first tick is what keeps a
   * substitution a lobby operation, which is where it belongs.
   */
  replaceOccupant(
    participantId: PvpParticipantId,
    accountId: string,
    appearance?: PvpAppearance,
  ): PvpRoomStep {
    const chair = this.requireChair(participantId);
    if (this.isOver()) {
      throw new PvpRoomError('room_is_over', `match ${this.matchId} is over`);
    }
    if (this.matchState === 'running') {
      throw new PvpRoomError(
        'replacement_after_start',
        `${participantId} cannot change hands once the match is running`,
      );
    }
    for (const other of this.orderedChairs) {
      if (other !== chair && other.accountId === accountId) {
        throw new PvpRoomError(
          'account_already_seated',
          `this account already holds ${other.participantId}`,
        );
      }
    }
    const closes = this.invalidateConnection(chair, 'superseded');
    chair.generation += 1;
    chair.accountId = accountId;
    chair.claimed = true;
    chair.ready = false;
    chair.disconnectedSince = null;
    if (appearance !== undefined) {
      chair.appearance = appearance;
    }
    /*
     * E o CASCO volta ao que nasceu (gate V28G.R).
     *
     * A aparência já era substituída porque é outra nave; o casco tinha ficado
     * para trás - a contagem de corações e as joias de quem saiu, e o teto de
     * vida que essas joias compuseram. Repô-lo é o que permite ao bilhete do
     * substituto congelar o casco DELE, pela primeira vez, quando ele se sentar.
     *
     * Antes do primeiro tique, sempre: a linha acima recusa uma substituição
     * depois de a partida começar.
     */
    this.simulation.resetParticipantHull(participantId);
    chair.hullFrozen = false;
    this.simulation.setParticipantConnected(participantId, false);
    this.settleWaitingState();
    return { frames: [], closes, finished: null, announcements: [] };
  }

  /* ------------------------------------------------------------------------ */
  /*  Connections                                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Attaches a connection to a chair.
   *
   * ## Two tabs produce one player
   *
   * A second connection for a chair that already has one REPLACES it: the older
   * connection is closed with `superseded` and the match carries on untouched.
   * It is not a second player, not a second chair and not a reason to end
   * anything - the human is still there, they just moved tab. Anything else
   * would let a refresh forfeit a match.
   *
   * ## What a new connection costs the neighbours: nothing
   *
   * Exactly one chair's generation advances. Nobody else's connection, sequence,
   * readiness or grace window is touched - which is the property that makes six
   * people reconnecting independently a normal afternoon rather than a cascade.
   */
  attachParticipant(
    participantId: PvpParticipantId,
    connectionId: string,
    now: number,
    options: {
      readonly accountId?: string;
      readonly appearance?: PvpAppearance;
      /** O nome público de quem se senta (gate V28H.R). Do bilhete assinado. */
      readonly displayName?: string;
      /**
       * Corações Primordiais incorporados na nave equipada de quem se senta.
       *
       * Vem do bilhete assinado, que o resolveu da base no momento em que foi
       * emitido. Um cliente não o envia e não tem por onde: o cabeçalho é posto
       * pelo Worker a partir das reivindicações verificadas.
       */
      readonly primordialHeartCount?: bigint;
      /**
       * As joias encaixadas na skin equipada de quem se senta (gate V28G.A).
       *
       * Vem do bilhete assinado, que a resolveu da base no momento em que foi
       * emitido. Um cliente não a envia e não tem por onde: o cabeçalho é posto
       * pelo Worker a partir das reivindicações verificadas.
       */
      readonly jewels?: JewelSocketProfile;
      /**
       * As três cartas que este piloto traz (gate V28H.J).
       *
       * Do bilhete assinado, como as joias: resolvidas na emissão, que é o
       * último sítio com uma base de dados aberta, e congeladas na primeira
       * ligação a sentar-se nesta cadeira.
       */
      readonly cards?: CardLoadout;
      /**
       * **A vida máxima autoritativa da conta de quem se senta** (V28G.S).
       *
       * O número FINAL, resolvido contra a base quando o bilhete foi emitido -
       * a vida-base, os pontos alocados, os artefactos, o equipamento e a soma
       * das percentagens com o Coração do Titã dentro, aplicada uma vez. A sala
       * não o recompõe e não tem por onde: aqui não há catálogo, nível de joia
       * nem ponto alocado.
       *
       * Um cliente não o envia e não tem por onde: o cabeçalho é posto pelo
       * Worker a partir das reivindicações verificadas.
       */
      readonly maxHealthMilli?: bigint;
    } = {},
  ): PvpRoomStep {
    const chair = this.requireChair(participantId);
    /*
     * Se o CASCO desta cadeira ainda está por congelar (gate V28G.R).
     *
     * Lido antes de tudo, e da CADEIRA e não de `accountId`. Era
     * `chair.accountId === null`, que respondia "a primeira pessoa a sentar-se
     * aqui" - e uma substituição preenche `accountId` no instante em que é
     * registada, antes de o substituto se ligar, portanto essa leitura dava
     * "não é a primeira" a quem ainda não tinha trazido casco nenhum.
     *
     * `replaceOccupant` repõe a nave e este sinalizador no mesmo passo, o que
     * faz a regra ler-se como ela é: um casco é congelado uma vez, e uma
     * substituição é a única coisa que o descongela.
     */
    const hullOpen = !chair.hullFrozen;
    if (options.accountId !== undefined) {
      this.claim(participantId, options.accountId);
    } else {
      chair.claimed = true;
    }
    /*
     * The profile is written ONCE, by the first connection to sit here.
     *
     * A reconnection carries its own freshly minted ticket, and that ticket
     * carries whatever the account's loadout says at that moment. Letting it
     * overwrite would mean a player could reshape their ship mid-match by
     * equipping in another tab and pressing F5 - the exact thing freezing is
     * for. A REPLACEMENT is the one thing that may change it, because it is a
     * different person.
     */
    /*
     * O nome, escrito uma vez e nunca reescrito - a mesma disciplina da
     * aparência logo abaixo. Uma reconexão traz o mesmo bilhete, e deixá-la
     * reescrever seria abrir a porta a um segundo bilhete com outro nome.
     */
    if (options.displayName !== undefined && chair.nickname === null) {
      chair.nickname = pvpNickname(options.displayName);
    }
    if (options.appearance !== undefined && chair.appearance === null) {
      chair.appearance = options.appearance;
    }
    /*
     * E o casco, pela MESMA regra e pela mesma razão (gate V28F.B).
     *
     * Escrito uma vez, pela primeira ligação a sentar-se aqui. Uma reconexão
     * traz um bilhete acabado de emitir, com a contagem que a conta tem NESSE
     * instante - e deixá-la sobrepor-se seria deixar um jogador incorporar num
     * separador e carregar em F5 para levar o coração novo para uma partida a
     * meio, que é exatamente aquilo de que congelar protege.
     */
    if (options.primordialHeartCount !== undefined && hullOpen) {
      this.simulation.freezeParticipantHearts(participantId, options.primordialHeartCount);
    }
    /*
     * E as joias, pela MESMA regra e pela mesma razão (gate V28G.A).
     *
     * Escritas uma vez, pela primeira ligação a sentar-se aqui. Uma reconexão
     * traz um bilhete acabado de emitir, com a ocupação que a skin tem NESSE
     * instante - e deixá-la sobrepor-se seria deixar um jogador encaixar seis
     * joias noutro separador e carregar em F5 para as levar para uma partida a
     * meio, que é exatamente aquilo de que congelar protege.
     *
     * Uma SUBSTITUIÇÃO muda-o, como muda a aparência, porque é outra pessoa a
     * sentar-se: a nave volta ao que nasceu e o bilhete do substituto congela o
     * casco DELE.
     */
    if (options.jewels !== undefined && hullOpen) {
      this.simulation.freezeParticipantJewels(participantId, options.jewels);
    }
    /*
     * E as três CARTAS, pela MESMA regra e pela mesma razão (gate V28H.J).
     *
     * Escritas uma vez, pela primeira ligação a sentar-se aqui. Uma reconexão
     * traz um bilhete acabado de emitir, com o loadout que a conta tem NESSE
     * instante - e deixá-lo sobrepor-se seria deixar alguém trocar de cartas
     * noutro separador e carregar em F5 para as levar para uma partida a meio.
     *
     * Uma SUBSTITUIÇÃO muda-as, como muda as joias, porque é outra pessoa a
     * sentar-se.
     */
    if (options.cards !== undefined && hullOpen) {
      this.simulation.freezeParticipantCards(participantId, options.cards);
    }
    /*
     * E a vida máxima, pela MESMA regra e pela mesma razão (gate V28G.S).
     *
     * Escrita uma vez, pela primeira ligação a sentar-se aqui. Uma reconexão
     * traz um bilhete acabado de emitir, com a vida máxima que a conta tem
     * NESSE instante - e deixá-la sobrepor-se seria deixar alguém gastar pontos
     * de vida noutro separador e carregar em F5 para levar o casco novo para
     * uma partida a meio, que é exatamente aquilo de que congelar protege.
     *
     * Uma SUBSTITUIÇÃO muda-a, como muda a aparência e as joias, porque é outra
     * pessoa a sentar-se: `resetParticipantHull` repõe a nave e o bilhete do
     * substituto congela a vida máxima DELE.
     *
     * A sala não a recompõe nem a consulta: ela chega pronta, e o único sítio
     * que a escreve é este.
     */
    if (options.maxHealthMilli !== undefined && hullOpen) {
      this.simulation.freezeParticipantMaxHealth(participantId, options.maxHealthMilli);
    }
    /*
     * E fecha, se alguma das três metades do casco chegou.
     *
     * As três, e não só as joias: um bilhete que traga corações e nenhuma joia
     * congelou casco na mesma, e deixar o sinalizador aberto deixaria a
     * reconexão seguinte trazer uma contagem nova.
     */
    if (
      hullOpen &&
      (options.primordialHeartCount !== undefined ||
        options.jewels !== undefined ||
        options.maxHealthMilli !== undefined)
    ) {
      chair.hullFrozen = true;
    }

    const closes = this.invalidateConnection(chair, 'superseded', connectionId);
    chair.generation += 1;
    chair.connectionId = connectionId;
    chair.disconnectedSince = null;
    /*
     * A returning socket has to declare itself ready again. The person who
     * pressed the button may not be the person now holding the tab, and a
     * readiness that survived the connection would let a room start under a
     * loading screen nobody is behind.
     */
    chair.ready = false;
    this.connections.set(connectionId, {
      connectionId,
      participantId,
      generation: chair.generation,
      windowStartedAt: now,
      windowCount: 0,
      dropped: 0,
    });
    this.simulation.setParticipantConnected(participantId, true);

    const completedTheRoom = this.settleWaitingState();
    const frames: PvpOutboundFrame[] = [{ connectionId, frame: this.welcomeFrame(participantId) }];
    /*
     * Everybody already here is told again when the ROSTER changes.
     *
     * A duel does it once, at the moment the room becomes complete - the
     * deadlock note below is why. A team match does it on every admission,
     * because every admission changes the public roster: a chair that was empty
     * now has somebody in it, with an appearance, and the five sockets already
     * connected have no other way to learn. It is the same welcome frame,
     * carrying the same fields, to a socket entitled to it.
     */
    const rosterChanged = this.ruleset !== PVP_DUEL_RULESET || completedTheRoom;
    if (rosterChanged) {
      /*
       * And everybody who was ALREADY here has to be told.
       *
       * This was a deadlock, and only two real clients could find it. The
       * challenger connects while the match is still `proposed` and is told
       * `waiting_for_opponent`. The opponent connects, the room becomes `ready`
       * - and the frame went to the newly attached socket alone. The challenger
       * never learned the room was complete, so it never showed a ready button,
       * so it never sent `ready`, so the start condition was never met and the
       * duel could not begin. Neither client could work around it: the
       * information simply never left the room.
       *
       * It is the same `welcome` frame, carrying the same fields, to a
       * connection that is entitled to it - no new frame type, no new field, no
       * contract change. A client that reconnects mid-match already has to
       * accept a fresh welcome and re-seat from `ack + 1`.
       */
      for (const other of this.orderedChairs) {
        if (other === chair || other.connectionId === null) {
          continue;
        }
        frames.push({
          connectionId: other.connectionId,
          frame: this.welcomeFrame(other.participantId),
        });
      }
    }
    return { frames, closes, finished: null, announcements: [] };
  }

  /**
   * Re-adopts a connection the runtime kept across a rebuild.
   *
   * Deliberately NOT {@link PvpRoom.attachParticipant}: attaching is a player
   * ARRIVING, which advances the generation, clears the grace timer, may
   * complete the room and sends a welcome. Re-attaching is the room discovering
   * that a connection it already had is still there, which changes nothing about
   * the match - it only restores the mapping between a connection id and a
   * chair, and it must NOT mint a generation, or every eviction would invalidate
   * every ticket the players are holding.
   *
   * Returns whether the connection was adopted. A chair already holding a
   * DIFFERENT live connection keeps it: two sockets for one chair is the
   * two-tabs case, and `attachParticipant` is where that is decided.
   */
  reattachParticipant(
    participantId: PvpParticipantId,
    connectionId: string,
    now: number,
    options: { readonly accountId?: string; readonly generation?: number } = {},
  ): boolean {
    const chair = this.requireChair(participantId);
    if (chair.connectionId !== null && chair.connectionId !== connectionId) {
      return false;
    }
    if (options.accountId !== undefined) {
      if (chair.accountId !== null && chair.accountId !== options.accountId) {
        return false;
      }
      chair.accountId = options.accountId;
    }
    chair.claimed = true;
    /*
     * The generation the SOCKET was minted with, when the caller still knows it
     * and the record does not. A version 2 room kept its generations in the
     * Durable Object's memory rather than on disk, so a room upgraded from one
     * would otherwise restart the counter underneath sockets that are still
     * holding a higher number - and the next attach would mint a generation
     * that had already been used.
     */
    if (options.generation !== undefined && options.generation > chair.generation) {
      chair.generation = options.generation;
    }
    chair.connectionId = connectionId;
    this.connections.set(connectionId, {
      connectionId,
      participantId,
      generation: chair.generation,
      windowStartedAt: now,
      windowCount: 0,
      dropped: 0,
    });
    chair.disconnectedSince = null;
    /*
     * A connection that survived a rebuild had already declared itself ready, or
     * it would not have been in a running match. A room still waiting will
     * simply hear `ready` again - the client repeats it on reconnect.
     */
    chair.ready = this.matchState === 'running';
    this.simulation.setParticipantConnected(participantId, true);
    return true;
  }

  /**
   * Drops every chair whose persisted connection is no longer live.
   *
   * ## Why a rebuild has to be told
   *
   * The logical connection is part of the room's record, so a rebuilt room
   * believes the chairs that were connected still are. That belief is right
   * after a hibernation - the runtime hands the same sockets back - and wrong
   * after a restart, where the sockets died with the process. A room that could
   * not tell the two apart would hold a match open for somebody who is not
   * there, and no grace window would ever start.
   *
   * So the transport re-attaches what it still holds and then says what that
   * was. Anything not in the list is treated exactly as if its socket had just
   * closed, which is what happened.
   */
  reconcileConnections(liveConnectionIds: readonly string[], now: number): void {
    const live = new Set(liveConnectionIds);
    for (const chair of this.orderedChairs) {
      if (chair.connectionId === null || live.has(chair.connectionId)) {
        continue;
      }
      this.connections.delete(chair.connectionId);
      chair.connectionId = null;
      this.simulation.setParticipantConnected(chair.participantId, false);
      if (this.matchState === 'running') {
        chair.disconnectedSince = chair.disconnectedSince ?? now;
      } else {
        chair.ready = false;
      }
    }
    if (this.matchState !== 'running') {
      this.settleWaitingState();
    }
  }

  /**
   * Detaches a connection.
   *
   * Ignores a connection that has already been superseded: the older tab's close
   * event arrives AFTER the newer tab attached, and treating it as this chair
   * leaving would disconnect a player who is sitting right there.
   */
  detach(connectionId: string, now: number): void {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return;
    }
    this.connections.delete(connectionId);
    const chair = this.chairs.get(connection.participantId);
    if (!chair || chair.connectionId !== connectionId) {
      return;
    }
    chair.connectionId = null;
    this.simulation.setParticipantConnected(chair.participantId, false);
    if (this.matchState === 'running') {
      /*
       * Only THIS chair. Five other people are still fighting, and a match that
       * paused or ended because one of six lost a socket would make dropping a
       * tactic.
       */
      chair.disconnectedSince = now;
      return;
    }
    /*
     * Nothing has been simulated, so there is nothing to forfeit. What a
     * departure costs before the first tick is the readiness that was declared,
     * because the next person to hold this chair has not agreed to anything.
     */
    chair.ready = false;
    this.settleWaitingState();
  }

  /* ------------------------------------------------------------------------ */
  /*  Messages                                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Handles one raw message from a connection.
   *
   * Size is checked BEFORE parsing: a megabyte of nonsense must cost a length
   * comparison, not a parse. Everything refused is counted, and a connection
   * that keeps producing refusals is closed - one burst is a lag spike, six
   * hundred is a script.
   */
  message(connectionId: string, raw: string, now: number): PvpRoomStep {
    const connection = this.connections.get(connectionId);
    if (!connection) {
      return emptyStep();
    }
    const chair = this.chairs.get(connection.participantId);
    /*
     * A connection from a superseded generation is not this participant
     * speaking, whatever its contents. It cannot normally exist - the older one
     * is removed when the newer attaches - and it is checked anyway, because
     * "unreachable" is a property of today's call order.
     */
    if (
      !chair ||
      chair.connectionId !== connectionId ||
      chair.generation !== connection.generation
    ) {
      return emptyStep();
    }
    if (raw.length > PVP_MAX_COMMAND_BYTES) {
      return this.refuse(connection, 'malformed');
    }
    if (now - connection.windowStartedAt >= 1_000) {
      connection.windowStartedAt = now;
      connection.windowCount = 0;
    }
    connection.windowCount += 1;
    if (connection.windowCount > PVP_MAX_COMMANDS_PER_SECOND) {
      return this.refuse(connection, 'rate_limited');
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return this.refuse(connection, 'malformed');
    }
    const frame = pvpClientFrameSchema.safeParse(parsed);
    if (!frame.success) {
      return this.refuse(connection, 'malformed');
    }

    /*
     * A spectator sends nothing that lands - not a command and not a readiness.
     *
     * Their hull is gone. A message from them is not hostile, it is a client
     * that has not noticed yet, so it is dropped rather than counted against
     * them. Checked before `ready` as well, because a wreck declaring itself
     * ready is exactly the shape a rematch would have to be, and this round has
     * no rematch.
     */
    if (this.isSpectator(chair.participantId)) {
      return emptyStep();
    }

    if (frame.data.t === 'ready') {
      /*
       * Idempotent per generation. A client repeats `ready` on every
       * reconnection and some repeat it while waiting; the second one must be
       * as free as it looks, and above all must not be a second chance to
       * start a room that is already starting.
       */
      if (chair.ready) {
        return emptyStep();
      }
      chair.ready = true;
      return this.maybeStart(now);
    }

    if (frame.data.t === 'leave') {
      /*
       * The player chose to abandon (ADR 0043).
       *
       * ## Who
       *
       * `chair` - derived above from the connection this frame arrived on,
       * which a signed one-use ticket bound to exactly one seat. The message
       * carries no participant, no team and no match, so there is nothing here
       * that could name somebody else.
       *
       * ## Idempotent
       *
       * A chair that has already left has already left. A double click, a
       * retry after a lost response and a replay of the same frame all take
       * this branch and cost nothing - no second announcement, no second
       * result, no second audit record.
       */
      if (chair.leftAt !== null) {
        return emptyStep();
      }
      /*
       * Before the fight, this is not an abandonment at all.
       *
       * A room that has not started has nothing to forfeit, and the lobby - not
       * the room - is what dissolves in that case. Marking the chair anyway
       * would let a player who backed out of a countdown be recorded as having
       * abandoned a match that never happened.
       */
      if (this.matchState !== 'running' && this.matchState !== 'ready') {
        return emptyStep();
      }
      chair.leftAt = now;
      /*
       * Evaluated NOW rather than left to the next pump.
       *
       * This is the whole reason the previous behaviour hung: when the last
       * socket goes, nothing drives the room's loop, so a check that only runs
       * on a tick never runs. The terminal decision is taken on the frame that
       * asked for it, by the same `checkAbandonment` an expired grace uses - so
       * a voluntary exit and a timed-out one end through one policy, not two.
       */
      const announcements: PvpRoomAnnouncement[] = [];
      if (this.checkAbandonment(now, announcements)) {
        return this.terminalStep([], announcements);
      }
      return { frames: [], closes: [], finished: null, announcements };
    }

    /*
     * A command before the room is complete is refused.
     *
     * `proposed` and `waiting_for_opponent` are the states where somebody is
     * still missing, and an intent applied there would move a sequence counter -
     * and set a movement - for a match that has not been agreed to. `ready` IS
     * accepted: everybody is here, the first tick is imminent, and the client
     * is already predicting.
     */
    if (this.matchState !== 'running' && this.matchState !== 'ready') {
      return emptyStep();
    }

    /*
     * A sequence far in the future is refused rather than applied.
     *
     * Applying it would let one frame set the counter to a number the client can
     * never legitimately reach again, after which every command it sends is
     * dropped as a replay. Counted like any other refusal, so a socket doing it
     * repeatedly is closed by the same threshold that closes any other script.
     */
    const ship = this.simulation.shipOf(chair.participantId);
    if (frame.data.seq > ship.appliedSeq + PVP_MAX_SEQUENCE_SKIP) {
      return this.refuse(connection, 'malformed');
    }

    /*
     * The participant comes from the CONNECTION, never from the message.
     *
     * There is no participant field in the command schema, so there is nothing
     * to forge: a connection can only ever move the ship it was attached to.
     */
    this.simulation.applyParticipantCommand(chair.participantId, {
      seq: frame.data.seq,
      mx: frame.data.mx,
      my: frame.data.my,
      ax: frame.data.ax,
      ay: frame.data.ay,
      firing: frame.data.f,
      teleportTo: frame.data.tp ?? null,
      /*
       * A ordem de DETONAR, encaminhada e nunca interpretada (gate V28G.N).
       *
       * Um booleano, e é tudo o que o cliente pode dizer: quem detona vem da
       * CONEXÃO, como tudo o resto neste método, e o que rebenta é decidido pela
       * simulação a partir de um estado que o cliente não escreve. Não há alvo,
       * contagem nem dano em lado nenhum desta mensagem.
       *
       * A sala não pergunta se há marcas: sem elas o tique faz um `no-op`, e
       * responder aqui seria uma segunda casa para a mesma regra.
       */
      detonateBleed: frame.data.bd === true,
      /*
       * **A ORDEM de ligar uma carta** (gate V28H.J).
       *
       * O espaço chega como 1, 2 ou 3 e é traduzido para o nome canónico aqui,
       * onde o protocolo e o domínio se encontram - o fio conta bytes, e o
       * domínio fala pelos nomes que o inventário usa.
       *
       * O participante é o da CONEXÃO, como tudo o resto neste método: não há
       * campo onde nomear outra pessoa, e é por isso que ninguém pode ligar a
       * carta do adversário.
       */
      activateCard:
        frame.data.ck === undefined
          ? null
          : {
              slot: PVP_CARD_SLOT_BY_NUMBER[frame.data.ck.s],
              commandId: frame.data.ck.id,
            },
      /* E a ordem de SOLTAR a carga, pelo mesmo caminho (gate V28H.N). */
      releaseCard:
        frame.data.cr === undefined
          ? null
          : {
              slot: PVP_CARD_SLOT_BY_NUMBER[frame.data.cr.s],
              commandId: frame.data.cr.id,
            },
      /*
       * **E a ordem de PILOTAR** (gate V28H.X), que não nomeia nada.
       *
       * Ela diz "embarca no que eu estou a tocar", e a sala é que decide o quê -
       * ou que não há nada para tocar. É a mesma disciplina das cartas: o
       * participante é o da CONEXÃO, e não há campo onde nomear outro.
       */
      boardSummon: frame.data.bo === undefined ? null : { commandId: frame.data.bo.id },
    });
    return emptyStep();
  }

  private refuse(connection: PvpConnection, reason: PvpCloseReason): PvpRoomStep {
    connection.dropped += 1;
    if (connection.dropped < PVP_RATE_LIMIT_CLOSE_THRESHOLD) {
      // Dropped silently. A refused command is not something the player did.
      return emptyStep();
    }
    this.connections.delete(connection.connectionId);
    const chair = this.chairs.get(connection.participantId);
    if (chair && chair.connectionId === connection.connectionId) {
      chair.connectionId = null;
      this.simulation.setParticipantConnected(chair.participantId, false);
    }
    return {
      frames: [],
      closes: [{ connectionId: connection.connectionId, reason }],
      finished: null,
      announcements: [],
    };
  }

  /* ------------------------------------------------------------------------ */
  /*  Starting                                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Moves the room between `proposed`, `waiting_for_opponent` and `ready`.
   *
   * Returns whether the room BECAME complete on this call - the moment every
   * chair has a connection and the first tick has not run. Ticking then would
   * spend somebody's loading screen standing still under fire.
   */
  private settleWaitingState(): boolean {
    if (this.matchState === 'running' || this.isOver()) {
      return false;
    }
    const connected = this.orderedChairs.filter((chair) => chair.connectionId !== null).length;
    if (connected === this.orderedChairs.length) {
      const completed = this.matchState !== 'ready';
      this.matchState = 'ready';
      return completed;
    }
    /*
     * Nobody had accepted yet. The proposal survives its own TTL instead of
     * dying because the challenger closed a tab.
     */
    this.matchState = connected === 0 ? 'proposed' : 'waiting_for_opponent';
    return false;
  }

  /**
   * Starts the match, once, when everybody is here and everybody has agreed.
   *
   * Every chair claimed, every chair connected, every chair ready. For a duel
   * that is two of each and for a team match six, and it is one sentence rather
   * than two because "the opponent" is not a thing a room with three ships a
   * side has.
   *
   * The final `ready` starts the match exactly once: the transition is guarded
   * by the state itself, so two clients whose messages arrive in either order,
   * or the same client's message arriving twice, produce one start.
   */
  private maybeStart(now: number): PvpRoomStep {
    if (this.matchState !== 'ready') {
      return emptyStep();
    }
    for (const chair of this.orderedChairs) {
      if (!chair.claimed || chair.connectionId === null || !chair.ready) {
        return emptyStep();
      }
    }
    this.matchState = 'running';
    // The accumulator starts NOW, not at construction: the seconds spent
    // waiting for an opponent are not seconds of combat.
    this.lastPumpAt = now;
    this.accumulatorMs = 0;
    this.lastBroadcastAt = now;
    const announcements: PvpRoomAnnouncement[] = [];
    if (!this.startAnnounced) {
      this.startAnnounced = true;
      announcements.push({ kind: 'room_started' });
    }
    const step = this.broadcastState();
    return { frames: step.frames, closes: [], finished: null, announcements };
  }

  /* ------------------------------------------------------------------------ */
  /*  The tick                                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Advances the room to `now` and returns what to send.
   *
   * ## The accumulator, and why the timer is not trusted
   *
   * Measured against real workerd, a Durable Object's `setInterval(50)` fires at
   * about 61.7 ms. If the tick were "whatever the timer delivered", the match
   * would run at 16 Hz on one runtime and 20 Hz on another, and a shot's travel
   * per tick would differ between them. So wall-clock time goes into an
   * accumulator and comes out as whole {@link PVP_TICK_MS} steps: a late timer
   * produces two ticks, never one long one.
   *
   * The catch-up is bounded. A runtime that stalled for a minute must not
   * resolve a minute of combat in one burst - the same reasoning as the hunt's
   * frame clamp.
   */
  pump(now: number): PvpRoomStep {
    const closes: PvpCloseInstruction[] = [];
    const announcements: PvpRoomAnnouncement[] = [];
    const abandonment = this.checkAbandonment(now, announcements);
    if (abandonment) {
      return this.terminalStep(closes, announcements);
    }
    if (this.matchState !== 'running') {
      return { frames: [], closes, finished: null, announcements };
    }
    if (this.lastPumpAt === null) {
      this.lastPumpAt = now;
      return { frames: [], closes, finished: null, announcements };
    }

    const elapsed = now - this.lastPumpAt;
    this.lastPumpAt = now;
    if (elapsed > 0) {
      this.accumulatorMs += Math.min(elapsed, PVP_MAX_CATCHUP_MS);
    }

    let ticked = false;
    while (this.accumulatorMs >= PVP_TICK_MS && this.simulation.teamResult === null) {
      this.accumulatorMs -= PVP_TICK_MS;
      this.simulation.advance();
      ticked = true;
    }
    /*
     * Presentation only, and drained so it cannot grow without bound in a room
     * whose clients are all gone. On the way past, the deaths are read: a hull
     * reaching zero is a fact about the MATCH, and the room is the only place
     * that can promise to state it once.
     */
    this.noteEliminations(this.drainDeaths(), 'combat', announcements);

    if (this.simulation.teamResult !== null) {
      /*
       * **O último quadro de ESTADO sai antes do fim** (gate V28G.N).
       *
       * ## O que se perdia
       *
       * `broadcastState` é onde os eventos de apresentação do tique são
       * esvaziados para o wire: os procs, as explosões e - desde este gate - as
       * detonações de sangramento. Devolver aqui apenas o quadro de fim
       * significava que o tique que DECIDE a partida nunca publicava nada.
       *
       * O golpe que mata é exatamente o golpe cujo efeito mais importa, e era o
       * único que ninguém via. O dono relatou-o pela Garra: «a nave foi
       * derrotada antes da animação de estouro acontecer». Não era antes - era
       * em vez de.
       *
       * ## Porque o estado E o fim, e nesta ordem
       *
       * São duas coisas diferentes e as duas são verdade: o estado descreve o
       * tique que acabou de correr - com a vida a zero e o evento que a pôs lá -
       * e o fim declara o resultado. O cliente aplica-os por ordem de chegada,
       * portanto desenha a morte com o seu efeito e só depois abre o painel.
       *
       * ## Uma vez, e não em cada `pump`
       *
       * Guardado por `announced`, que é a mesma bandeira que impede o quadro de
       * fim de ser anunciado duas vezes. Sem ela, uma sala já terminada voltaria
       * a publicar estado a cada mensagem que chegasse.
       *
       * A estrangulação de `lastBroadcastAt` NÃO se aplica: ela existe para não
       * dizer três vezes a mesma coisa por tique, e isto é a última coisa que
       * esta sala tem para dizer.
       */
      const farewell = this.announced ? [] : this.broadcastState().frames;
      const terminal = this.terminalStep(closes, announcements);
      return { ...terminal, frames: [...farewell, ...terminal.frames] };
    }
    if (!ticked) {
      return { frames: [], closes, finished: null, announcements };
    }
    /*
     * At most one frame per tick, however often `pump` is called.
     *
     * The room is driven by INCOMING MESSAGES rather than by a timer, and two
     * players sending input at 30 Hz call this sixty times a second.
     * Broadcasting on every call would triple a match's bandwidth to say the
     * same thing three times. The simulation still advances on every call - what
     * is throttled is the telling, not the world.
     */
    if (now - this.lastBroadcastAt < PVP_TICK_MS) {
      return { frames: [], closes, finished: null, announcements };
    }
    this.lastBroadcastAt = now;
    const step = this.broadcastState();
    return { frames: step.frames, closes, finished: null, announcements };
  }

  /**
   * The participants whose hulls fell since the last drain, canonical order.
   *
   * **E, na mesma passagem, os procs confirmados.** A drenagem é o único sítio
   * onde os eventos da simulação são vistos, e ela acontece em CADA pump - mais
   * vezes do que uma publicação, porque a difusão está limitada a um frame por
   * tique. Um proc drenado e não guardado seria um corte que aconteceu e que
   * nenhum cliente soube: é por isso que ele vai para uma caixa de saída em vez
   * de ser lido e deitado fora, como era até este gate.
   */
  private drainDeaths(): readonly PvpParticipantId[] {
    const fallen: PvpParticipantId[] = [];
    for (const event of this.simulation.drainParticipantEvents()) {
      if (event.kind === 'death') {
        fallen.push(event.participantId);
      }
      if (event.kind === 'proc') {
        this.pendingProcs.push({
          i: event.projectileId,
          e: event.procId,
          p: event.participantId,
          q: event.targetId,
          /* O tique do EVENTO, nunca o da publicação que o vai levar. */
          t: event.tick,
          /*
           * A magnitude da cura, quando o efeito é uma. Copiada e nunca
           * derivada: o cliente não tem por onde a recalcular.
           */
          ...(event.healedMilli === undefined ? {} : { h: event.healedMilli }),
          x: event.x,
          y: event.y,
        });
      }
      if (event.kind === 'bleed') {
        this.pendingBleeds.push({
          p: event.participantId,
          /* O tique do EVENTO, nunca o da publicação que o vai levar. */
          t: event.tick,
          q: event.targets.map((target) => ({
            s: target.targetId,
            n: target.marks,
            d: target.applied,
          })),
        });
      }
      if (event.kind === 'blast') {
        this.pendingBlasts.push({
          i: event.projectileId,
          e: event.effectId,
          p: event.participantId,
          /* O tique do EVENTO, nunca o da publicação que o vai levar. */
          t: event.tick,
          x: event.x,
          y: event.y,
          /* O raio MECÂNICO. O cliente desenha este e não um seu. */
          r: event.radius,
          q: [...event.hit],
        });
      }
      if (event.kind === 'reflect') {
        this.pendingReflects.push({
          e: event.effectId,
          p: event.participantId,
          q: event.targetId,
          /* O tique do EVENTO, nunca o da publicação que o vai levar. */
          t: event.tick,
          k: event.payload,
          s: event.stage === 'intercepted' ? 'i' : 'l',
          x: event.x,
          y: event.y,
          tx: event.tx,
          ty: event.ty,
          /*
           * O dano, quando o payload redirecionado já aterrou. Copiado e nunca
           * derivado: o cliente não tem as magnitudes congeladas, e a barra
           * move-se também por tiros do mesmo tique.
           */
          ...(event.applied === undefined ? {} : { d: event.applied }),
        });
      }
    }
    return fallen;
  }

  /**
   * Os procs confirmados à espera da próxima publicação, e depois esvaziados.
   *
   * ## Porque uma caixa de saída, e não um campo no frame
   *
   * Porque a sala simula a 20 Hz e publica à cadência de envio do cliente, que
   * pode ser mais lenta - `room-publish-rate.test.ts` mede-o. Um frame carrega
   * portanto MAIS DE UM TIQUE, e dois cortes podem acontecer entre duas
   * publicações. Guardar só o último perderia o outro em silêncio.
   *
   * ## Porque NÃO é persistida
   *
   * Porque é apresentação, e apresentação não decide o futuro de uma partida:
   * `PvpRoomPersistedState` continua na versão que está. Uma sala que hiberne
   * entre a drenagem e a publicação perde, no máximo, o anúncio de um frame - e
   * um anúncio perdido é preferível a um anúncio ressuscitado, que desenharia um
   * corte muito depois de a vida já ter descido.
   */
  private pendingProcs: PvpProcOutbox[] = [];

  /** A caixa de saída das explosões. A gémea da de cima (gate V28G.J). */
  private pendingBlasts: PvpBlastOutbox[] = [];

  /** E a das detonações de sangramento, pela mesma razão (gate V28G.N). */
  private pendingBleeds: PvpBleedOutbox[] = [];

  /** E a das reflexões, pela mesma razão (gate V28G.O). */
  private pendingReflects: PvpReflectOutbox[] = [];

  /** Partilhada e congelada: uma publicação sem reflexões não aloca. */
  private static readonly NO_REFLECTS: readonly PvpReflectOutbox[] = Object.freeze([]);

  /** Esvazia a caixa de saída das reflexões. Uma vez por publicação. */
  private takeReflects(): readonly PvpReflectOutbox[] {
    if (this.pendingReflects.length === 0) {
      return PvpRoom.NO_REFLECTS;
    }
    const taken = this.pendingReflects;
    this.pendingReflects = [];
    return taken;
  }

  /** Partilhada e congelada: uma publicação sem detonações não aloca. */
  private static readonly NO_BLEEDS: readonly PvpBleedOutbox[] = Object.freeze([]);

  /** Partilhada e congelada: uma publicação sem explosões não aloca. */
  private static readonly NO_BLASTS: readonly PvpBlastOutbox[] = Object.freeze([]);

  /** Partilhada e congelada: uma publicação sem procs não aloca. */
  private static readonly NO_PROCS: readonly PvpProcOutbox[] = Object.freeze([]);

  /** Esvazia a caixa de saída. Chamado exatamente uma vez por publicação. */
  private takeProcs(): readonly PvpProcOutbox[] {
    if (this.pendingProcs.length === 0) {
      return PvpRoom.NO_PROCS;
    }
    const taken = this.pendingProcs;
    this.pendingProcs = [];
    return taken;
  }

  /** Esvazia a caixa das explosões. Chamada uma vez por publicação. */
  private takeBlasts(): readonly PvpBlastOutbox[] {
    if (this.pendingBlasts.length === 0) {
      return PvpRoom.NO_BLASTS;
    }
    const taken = this.pendingBlasts;
    this.pendingBlasts = [];
    return taken;
  }

  /** Esvazia a caixa das detonações. Chamada uma vez por publicação. */
  private takeBleeds(): readonly PvpBleedOutbox[] {
    if (this.pendingBleeds.length === 0) {
      return PvpRoom.NO_BLEEDS;
    }
    const taken = this.pendingBleeds;
    this.pendingBleeds = [];
    return taken;
  }

  private noteEliminations(
    fallen: readonly PvpParticipantId[],
    cause: 'combat' | 'abandonment',
    announcements: PvpRoomAnnouncement[],
  ): void {
    for (const participantId of fallen) {
      const chair = this.chairs.get(participantId);
      if (!chair || chair.eliminationAnnounced) {
        continue;
      }
      chair.eliminationAnnounced = true;
      announcements.push({
        kind: 'participant_eliminated',
        participantId,
        team: chair.team,
        cause,
      });
    }
  }

  /**
   * Ends the match because the room itself failed.
   *
   * NO winner, on purpose. If the room cannot vouch for its own state it cannot
   * say who was winning, and a result invented from the last frame it happens to
   * remember is a guess wearing a result's clothes.
   */
  failService(): PvpRoomStep {
    this.simulation.finishTeam('service_failure', null);
    return this.terminalStep([], []);
  }

  /** The challenger withdrew before anybody accepted. */
  cancel(): PvpRoomStep {
    if (this.matchState === 'proposed' || this.matchState === 'waiting_for_opponent') {
      this.matchState = 'cancelled';
      this.simulation.finishTeam('cancelled_by_challenger', null);
      return this.terminalStep([], []);
    }
    return emptyStep();
  }

  /** Nobody accepted before the deadline. */
  expire(): PvpRoomStep {
    if (this.matchState === 'proposed' || this.matchState === 'waiting_for_opponent') {
      this.matchState = 'expired';
      this.simulation.finishTeam('expired_unaccepted', null);
      return this.terminalStep([], []);
    }
    return emptyStep();
  }

  /* ------------------------------------------------------------------------ */
  /*  Abandonment                                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Turns a stayed-away participant into a loss, and says whether that ended it.
   *
   * A chair that comes back inside the grace window costs nothing at all - the
   * ship kept its position, its health and every shot in the air, because the
   * simulation never stopped and never restarted.
   *
   * ## The two rules, and why they are two
   *
   * A **duel** ends outright: with one ship a side, the ship being gone and the
   * match being over are the same event, and the tie-break when BOTH walk away
   * is "whoever stayed longer wins", which is a sentence about two people.
   *
   * A **team match** eliminates the individual hull and keeps going. The first
   * and second losses change who is shooting and nothing else; the third wipes a
   * team and ends it. There is no "who stayed longer" here, deliberately: with
   * three a side the last hull to fall is not the same question as who left
   * first, and inventing an ordering over six timestamps would be picking a
   * winner by a rule nobody agreed to. Both teams wiped in the same resolution
   * is therefore a draw - the deterministic answer, and the honest one.
   */
  private checkAbandonment(now: number, announcements: PvpRoomAnnouncement[]): boolean {
    if (this.matchState !== 'running' || this.simulation.teamResult !== null) {
      return false;
    }
    const expired = this.orderedChairs.filter(
      (chair) =>
        /*
         * Two ways to be gone, and only one of them waits (ADR 0043).
         *
         * A player who pressed the button is gone NOW: the grace exists to
         * protect somebody whose socket failed, and nothing about a deliberate
         * choice needs protecting from itself. A player whose socket dropped
         * still gets the whole window, unchanged.
         */
        (chair.leftAt !== null ||
          (chair.disconnectedSince !== null &&
            now - chair.disconnectedSince >= PVP_RECONNECT_GRACE_MS)) &&
        /*
         * A hull that is already down cannot be abandoned again. Somebody who
         * died and then closed the tab is one loss: a second one would draw a
         * second explosion over a wreck and, worse, count twice towards their
         * team being wiped.
         */
        this.simulation.shipOf(chair.participantId).healthMilli > 0n,
    );
    if (expired.length === 0) {
      return false;
    }
    for (const chair of expired) {
      if (!chair.abandonAnnounced) {
        chair.abandonAnnounced = true;
        announcements.push({
          kind: 'participant_abandoned',
          participantId: chair.participantId,
          team: chair.team,
        });
      }
    }
    if (this.ruleset === PVP_DUEL_RULESET) {
      return this.finishDuelAbandonment(new Set(expired.map((chair) => chair.participantId)));
    }
    /*
     * A team match ends when somebody CHOOSES to go (ADR 0043).
     *
     * ## Why this is not an elimination
     *
     * The rule below - eliminate the hull and carry on - is right for a grace
     * window that expired: a lost connection is not a decision, the other five
     * are still playing, and the match can honestly continue while somebody
     * tries to come back. A deliberate exit is different in kind. The simulation
     * is built for three a side and there is no 2x3 contract: no balance for it,
     * no result text for it, and no agreement that it is a match anybody
     * consented to play. Continuing would be inventing a game mode inside an
     * abandonment.
     *
     * So the team that lost a player by choice loses the match, under the
     * result the room already has for an abandonment. Which player it was does
     * not change the answer, so the first one found decides it - and any others
     * arriving in the same instant find a match that is already over, which is
     * what makes six simultaneous presses produce one result.
     */
    const walkedOut = expired.find((chair) => chair.leftAt !== null);
    if (walkedOut !== undefined) {
      if (!walkedOut.abandonAnnounced) {
        walkedOut.abandonAnnounced = true;
        announcements.push({
          kind: 'participant_abandoned',
          participantId: walkedOut.participantId,
          team: walkedOut.team,
        });
      }
      this.simulation.finishTeam(
        'abandonment',
        walkedOut.team === 'a' ? pvpDuelSeatToTeam('b') : pvpDuelSeatToTeam('a'),
        abandonmentOf(walkedOut),
      );
      return true;
    }
    /*
     * From here every chair in `expired` ran out of GRACE: the voluntary branch
     * above returns before reaching this. The cause is still read off each chair
     * by `abandonmentOf` rather than assumed here, so a later branch cannot make
     * that sentence silently false.
     */
    for (const chair of expired) {
      if (this.simulation.eliminateParticipant(chair.participantId)) {
        this.noteEliminations([chair.participantId], 'abandonment', announcements);
      }
    }
    /*
     * The death events this produced are drained here rather than left for the
     * pump, which never reaches this line: the announcements above already say
     * everything a listener needs, and letting them sit would replay somebody's
     * explosion on the next tick.
     */
    this.simulation.drainParticipantEvents();
    const standing = this.standingTeams();
    if (standing.length === 1) {
      const winner = standing[0];
      if (winner !== undefined) {
        /*
         * WHICH of the expired chairs is named, and why it is a rule rather
         * than a pick.
         *
         * A wipe can be completed by any of three graces running out in the same
         * resolution, and "the last one processed" is an ordering nobody agreed
         * to. So it is the first chair on the LOSING side in canonical order -
         * team then slot, the order `orderedChairs` already produces - which is
         * the same answer in every rebuild and on every one of the six screens.
         */
        const decisive = expired.find((chair) => chair.team !== winner) ?? expired[0];
        this.simulation.finishTeam(
          'abandonment',
          winner,
          decisive === undefined ? null : abandonmentOf(decisive),
        );
        return true;
      }
    }
    if (standing.length === 0) {
      const decisive = expired[0];
      this.simulation.finishTeam(
        'abandonment',
        null,
        decisive === undefined ? null : abandonmentOf(decisive),
      );
      return true;
    }
    return false;
  }

  /**
   * The duel's abandonment rule, unchanged since it was written for two seats.
   *
   * Kept literally rather than derived from the team rule, because it is not a
   * special case of it: the tie-break rewards whoever stayed longer, which is a
   * comparison between exactly two instants.
   */
  private finishDuelAbandonment(expired: ReadonlySet<PvpParticipantId>): boolean {
    const a = pvpDuelSeatToParticipantId('a');
    const b = pvpDuelSeatToParticipantId('b');
    /*
     * WHEN each of them went, by whichever way they went (ADR 0043).
     *
     * A voluntary leaver may never have been disconnected, so reading
     * `disconnectedSince` alone would read `null` for them and send the
     * comparison down the "one of them stayed" branch - handing the win to
     * somebody who also walked out. The instant of the choice is the instant
     * they went.
     */
    const wentAt = (id: PvpParticipantId): number | null => {
      const chair = this.requireChair(id);
      return chair.leftAt ?? chair.disconnectedSince;
    };
    const goneA = wentAt(a);
    const goneB = wentAt(b);
    if (expired.has(a) && expired.has(b) && goneA !== null && goneB !== null) {
      /*
       * Both walked away. The one who stayed longer wins; if they left on the
       * very same millisecond nobody does. There is no reward attached to this
       * outcome, which is exactly why it can afford to be honest.
       */
      const winner =
        goneA === goneB ? null : goneA > goneB ? pvpDuelSeatToTeam('a') : pvpDuelSeatToTeam('b');
      /*
       * And the one who went FIRST is the one who produced the ending.
       *
       * Not the winner, and not "whichever socket this room noticed closing
       * last": the match ended because somebody went, and the earlier instant is
       * the one that started it. An exact tie falls to canonical order, which is
       * the only tie-break this room is allowed to use - both of them abandoned,
       * so naming either says something true to all four readings of the frame.
       */
      this.simulation.finishTeam(
        'abandonment',
        winner,
        abandonmentOf(this.requireChair(goneA <= goneB ? a : b)),
      );
      return true;
    }
    /*
     * One of them stayed away. The OTHER wins - including when they too are
     * disconnected, as long as their own grace window is still running: coming
     * back one second before the deadline is coming back.
     */
    this.simulation.finishTeam(
      'abandonment',
      expired.has(a) ? pvpDuelSeatToTeam('b') : pvpDuelSeatToTeam('a'),
      abandonmentOf(this.requireChair(expired.has(a) ? a : b)),
    );
    return true;
  }

  /** Which teams still have a hull in the arena. Canonical order. */
  private standingTeams(): readonly PvpTeam[] {
    const ships = this.simulation.participants();
    return PVP_TEAMS.filter((team) =>
      ships.some((ship) => ship.team === team && ship.healthMilli > 0n),
    );
  }

  /* ------------------------------------------------------------------------ */
  /*  Ending                                                                  */
  /* ------------------------------------------------------------------------ */

  private terminalStep(
    closes: PvpCloseInstruction[],
    announcements: PvpRoomAnnouncement[],
  ): PvpRoomStep {
    const result = this.simulation.teamResult;
    if (result === null) {
      return { frames: [], closes, finished: null, announcements };
    }
    if (this.matchState !== 'cancelled' && this.matchState !== 'expired') {
      this.matchState = 'completed';
    }
    if (this.announced) {
      return { frames: [], closes, finished: null, announcements };
    }
    this.announced = true;
    announcements.push(this.endingAnnouncement(result), { kind: 'room_ended', result });
    const duel = this.ruleset === PVP_DUEL_RULESET ? pvpTeamResultToDuelResult(result) : null;
    /*
     * The ending, in whichever protocol this room speaks.
     *
     * A duel names a winning SEAT because its winning team has exactly one
     * person; a team match names the TEAM, because three people won and none of
     * them is "seat a". The same distinction the database draws, on the wire.
     */
    const ending: PvpOutboundPayload =
      duel === null
        ? {
            t: 'ended',
            v: PVP_TEAM_PROTOCOL_VERSION,
            state: this.matchState,
            winnerTeam: result.winner === null ? null : (result.winner as 'a' | 'b'),
            reason: result.reason,
            ticks: result.ticks,
            /*
             * WHY, for the six clients to read against their own chair.
             *
             * A fact and not a sentence: the cause, the chair and its side. Who
             * that is to the reader - themselves, a team-mate, an opponent - is
             * the reader's comparison to make, and the room does not know which
             * of the six it is talking to at the moment it decides.
             */
            ab: this.abandonmentFrame(result),
          }
        : ({
            t: 'ended',
            v: PVP_DUEL_PROTOCOL_VERSION,
            state: this.matchState,
            winner: duel.winner,
            reason: duel.reason,
            ticks: duel.ticks,
            abandon: duel.abandonment ?? null,
          } as PvpServerFrame);
    const frames: PvpOutboundFrame[] = [...this.connections.values()].map((connection) => ({
      connectionId: connection.connectionId,
      frame: ending,
    }));
    return { frames, closes, finished: result, announcements };
  }

  /**
   * The abandonment as the team wire carries it, or `null`.
   *
   * The team letter comes from the CHAIR rather than from the id's first
   * character: the id is derived from the pair everywhere it is produced, and a
   * reader parsing one to pick a side would be a second opinion about who is on
   * which team. `requireChair` throws for an id the room does not have, which is
   * a result naming nobody and must never become a frame.
   */
  private abandonmentFrame(result: PvpTeamResult): PvpTeamAbandonmentFrame | null {
    if (result.abandonment === null) {
      return null;
    }
    const chair = this.requireChair(result.abandonment.participantId);
    return {
      cause: result.abandonment.cause,
      p: chair.participantId,
      tm: chair.team as 'a' | 'b',
    };
  }

  /**
   * Which of the four endings this was.
   *
   * Total and mutually exclusive by construction: a service failure never names
   * a winner, the time limit is a draw by the rules rather than a win on points,
   * and everything else either has a winning team or does not.
   */
  private endingAnnouncement(result: PvpTeamResult): PvpRoomAnnouncement {
    if (result.reason === 'service_failure') {
      return { kind: 'service_failure' };
    }
    if (result.winner !== null) {
      return { kind: 'team_won', team: result.winner };
    }
    return result.reason === 'time_limit' ? { kind: 'timeout' } : { kind: 'draw' };
  }

  /* ------------------------------------------------------------------------ */
  /*  Frames                                                                  */
  /* ------------------------------------------------------------------------ */

  /**
   * The state frame, for every connection entitled to one.
   *
   * ## A team match sends nothing, and that is the point
   *
   * There is no six-participant wire: the schemas, the ticket and the row all
   * say `a` and `b`. A room that invented an addressing for six would be a room
   * whose frames nothing could read, and the first thing to read them would
   * decide what they meant. So a `team_3v3` simulates, decides, announces and
   * persists - and sends nothing at all until the wire that can carry it exists.
   * {@link PvpRoom.audience} is where "who would receive this" already lives, so
   * the day the wire lands there is one list to encode and no rule to rewrite.
   */
  /**
   * The public world plus one envelope per listening socket.
   *
   * Built once and addressed six times. `ack` and `gen` are the ONLY things
   * that differ between the six frames, which is what makes "the six clients
   * agree about the world" a property of the construction rather than of six
   * separate encodings that happen to match.
   *
   * A spectator is in `audience()` and therefore gets one. A superseded
   * connection is not in `this.connections` and therefore does not.
   */
  private broadcastTeamState(): PvpRoomStep {
    /*
     * **Um mundo por EQUIPA, e já não um para os seis** (gate V28H.L).
     *
     * O Manto Espectral obriga: um casco invisível é omitido do mundo de quem
     * não é da equipa dele, e o que decide isso é a equipa de quem vê. Dois
     * mundos, e não seis - `ack` e `gen` continuam a ser a única coisa que
     * difere DENTRO de uma equipa, que é o que mantém a propriedade original:
     * os clientes de um lado concordam sobre o mundo por construção.
     */
    /*
     * **Os quatro sorvedouros são lidos UMA vez, e antes dos mundos.**
     *
     * `takeProcs`, `takeBlasts`, `takeBleeds` e `takeReflects` ESVAZIAM as suas
     * caixas de saída. Enquanto havia um mundo só isso era invisível; com um
     * mundo por equipa, chamá-los lá dentro daria tudo ao primeiro lado e nada
     * ao segundo - metade da sala deixava de ver metade dos efeitos.
     *
     * Lidos aqui, os dois mundos carregam a MESMA lista, e a publicação seguinte
     * começa vazia, que é exactamente o que acontecia antes.
     */
    const drainedProcs = this.takeProcs();
    const drainedBlasts = this.takeBlasts();
    const drainedBleeds = this.takeBleeds();
    const drainedReflects = this.takeReflects();
    const worldFor = (viewer: PvpTeam) =>
      pvpTeamWorldOf({
        viewer,
        tick: this.simulation.tick,
        state: this.matchState,
        ships: this.simulation.participants(),
        projectiles: this.simulation.projectiles(),
        /* Os mísseis em voo, ao lado dos tiros e nunca misturados com eles. */
        missiles: this.simulation.missilesInFlight(),
        /* E as invocações, na sua própria lista (gate V28H.L). */
        summons: this.simulation.summonList(),
        runes: this.simulation.runeList(),
        appearance: this.publicAppearance(),
        /*
         * Esvaziada AQUI, e uma vez só.
         *
         * Os seis frames desta publicação carregam a mesma lista, e a publicação
         * seguinte começa vazia. Ler sem esvaziar repetiria cada corte em todos
         * os frames até ao fim da partida.
         */
        procs: drainedProcs.map((proc) => ({
          i: proc.i,
          e: proc.e,
          p: proc.p,
          q: proc.q,
          t: proc.t,
          /* A cura, em MILLI e como string decimal (gate V28G.S.1). */
          ...(proc.h === undefined ? {} : { h: encodeHealthMilli(proc.h) }),
          x: proc.x,
          y: proc.y,
        })),
        blasts: drainedBlasts.map((blast) => ({ ...blast, q: [...blast.q] })),
        /*
         * Os stacks de veneno, LIDOS e nunca esvaziados (gate V28G.M).
         *
         * A diferença com as duas listas acima é a diferença entre estado e
         * evento: um proc esvazia-se ao ser publicado porque já aconteceu, e um
         * stack continua a existir depois de o frame sair. Esvaziá-lo apagaria a
         * marca do ecrã na publicação seguinte.
         */
        poison: this.simulation.poisonStacks(),
        /*
         * As marcas, LIDAS e nunca esvaziadas, pela mesma razão que os stacks.
         * Elas continuam a existir depois de o frame sair (gate V28G.N).
         */
        bleeding: this.simulation.bleedingMarks(),
        /* E as detonações, ESVAZIADAS: elas aconteceram e acabaram. */
        bleedBursts: drainedBleeds,
        /*
         * E as reflexões, ESVAZIADAS pela mesma razão (gate V28G.O).
         *
         * Já endereçadas por PARTICIPANTE, que é o vocabulário deste frame: a
         * caixa de saída guarda ids e é o duelo que os traduz para assentos.
         */
        reflects: drainedReflects.map((reflect) => ({
          e: reflect.e,
          p: reflect.p,
          q: reflect.q,
          t: reflect.t,
          k: reflect.k,
          s: reflect.s,
          x: reflect.x,
          y: reflect.y,
          tx: reflect.tx,
          ty: reflect.ty,
          /* O dano aplicado, em MILLI e como string decimal (gate V28G.S.1). */
          ...(reflect.d === undefined ? {} : { d: encodeHealthMilli(reflect.d) }),
        })),
      });
    /* Dois, e construídos uma vez cada: seis ligações partilham dois mundos. */
    const worlds = new Map<PvpTeam, ReturnType<typeof worldFor>>(
      PVP_TEAMS.map((team) => [team, worldFor(team)]),
    );
    const frames: PvpOutboundFrame[] = [];
    for (const connection of this.connections.values()) {
      const chair = this.chairs.get(connection.participantId);
      if (!chair) {
        continue;
      }
      const world = worlds.get(chair.team);
      if (world === undefined) {
        continue;
      }
      frames.push({
        connectionId: connection.connectionId,
        frame: pvpTeamStateFrameOf(world, this.selfOf(chair)),
      });
    }
    return { frames, closes: [], finished: null, announcements: [] };
  }

  /** The appearance of every chair, by identity, with `null` for the unarrived. */
  private publicAppearance(): ReadonlyMap<PvpParticipantId, PvpAppearance | null> {
    return new Map(this.orderedChairs.map((chair) => [chair.participantId, chair.appearance]));
  }

  /** One chair's private half. Never built for anybody but its own socket. */
  private selfOf(chair: Chair): PvpTeamSelf {
    return pvpTeamSelfOf({
      participantId: chair.participantId,
      team: chair.team,
      slot: chair.slot,
      generation: chair.generation,
      appliedSeq: this.simulation.shipOf(chair.participantId).appliedSeq,
      /* Os três espaços DESTA cadeira, e nunca os de outra (gate V28H.J). */
      cards: this.simulation.cardSlotsOf(chair.participantId),
    });
  }

  /**
   * A team welcome, which says what is true before six ships exist.
   *
   * The first socket may arrive before the other five, so the roster carries all
   * six CHAIRS with their connection and readiness, and an appearance that is
   * null until its holder has presented a capability. Nothing here pretends the
   * simulation is already six ships, and nothing invents an occupant.
   */
  private teamWelcomeFrame(participantId: PvpParticipantId): PvpOutboundPayload {
    const chair = this.requireChair(participantId);
    return pvpTeamWelcomeOf({
      matchId: this.matchId,
      mode: this.mode,
      state: this.matchState,
      self: this.selfOf(chair),
      roster: pvpTeamRosterOf(
        this.orderedChairs.map((entry) => ({
          participantId: entry.participantId,
          team: entry.team as 'a' | 'b',
          slot: entry.slot,
          connected: entry.connectionId !== null,
          ready: entry.ready,
          appearance: entry.appearance,
          /* E o nome, da cadeira - que é onde uma PESSOA vive (gate V28H.R). */
          nickname: entry.nickname,
          // From the simulation, which is where a hull lives - not from the
          // chair, which is where a PERSON lives.
          combatProfile: pvpCombatProfileToWire(
            this.simulation.shipOf(entry.participantId).profile,
          ),
        })),
      ),
    });
  }

  private broadcastState(): PvpRoomStep {
    if (this.ruleset !== PVP_DUEL_RULESET) {
      return this.broadcastTeamState();
    }
    /*
     * **Os cascos, POR EQUIPA que os vê** (gate V28H.L).
     *
     * O frame do duelo já era construído por ligação - o `ack` e os três
     * espaços sempre foram de quem os recebe - e agora a LISTA também é: um
     * casco com o Manto Espectral é **omitido** do frame do adversário, e não
     * marcado nele. Marcá-lo seria mandar a posição de uma nave invisível para o
     * cliente de quem não a devia ver, e um cliente modificado desenhava-a.
     *
     * Duas listas e não seis: o que decide é a EQUIPA de quem vê, e um duelo tem
     * duas. Os aliados continuam a ver o seu.
     */
    const shipsFor = (viewer: PvpTeam) =>
      this.simulation
        .participants()
        .filter((ship) => !this.simulation.isCloakedFrom(ship.participantId, viewer))
        .map((ship) => ({
          s: pvpParticipantIdToDuelSeat(ship.participantId),
          x: ship.x,
          y: ship.y,
          ax: ship.aimX,
          ay: ship.aimY,
          hp: encodeHealthMilli(ship.healthMilli),
          c: ship.connected,
          /* Ausente quando ninguém o empurra, que é o estado normal (gate V28G.J). */
          ...(ship.knockback === null ? {} : { kb: ship.knockback.ticks }),
          /* As serras, ausentes em todo o casco sem a joia (gate V28G.P). */
          ...pvpSawFrameFields(ship),
          /*
           * Os dois canhões temporários, ausentes na maior parte do tempo (V28H.J).
           *
           * Público, e tem de ser: eles disparam contra quem está do outro lado, e
           * um adversário que não os visse levaria tiros de uma arma invisível. O
           * que NÃO é público é o arrefecimento, que viaja na metade privada.
           */
          ...(shipHasSuperCannons(ship) ? { sc: 1 as const } : {}),
          /*
           * E os dois factos públicos das cartas de temporizador (gate V28H.L).
           *
           * Ausentes na maior parte do tempo, como tudo o que é raro neste frame.
           */
          ...pvpCardFlagFields(this.simulation.publicCardFlagsOf(ship.participantId)),
          /*
           * **E o que as cartas dele desenham** (gates V28H.L, V28H.N e V28H.O).
           *
           * Isto FALTAVA no frame do duelo, e o import já cá estava sem uso: o
           * 3x3 publicava `cv` e o 1x1 não, portanto as mesmas cartas desenhavam
           * num modo e não no outro. A leitura é a partilhada, para os dois
           * protocolos dizerem a mesma coisa por construção e não por acaso.
           */
          ...pvpCardVisualFields(this.simulation.cardVisualStateOf(ship.participantId)),
        }));
    const proj = this.simulation.projectiles().map((projectile) => ({
      i: projectile.id,
      x: projectile.x,
      y: projectile.y,
      s: pvpParticipantIdToDuelSeat(projectile.ownerParticipantId),
      /* Ausente num tiro comum, que é quase todo (gate V28G.J). */
      ...(projectile.atomicBomb === null ? {} : { b: true as const }),
      /*
       * E a bomba de uma CARTA diz qual é e quanto vai abrir (gate V28H.U).
       *
       * O raio da EXPLOSÃO, que é o que a autoridade possui: o corpo desenhado é
       * uma fracção dele, e assim a bola que se vê é a bola que apanha. A Esfera
       * cresce com a carga, portanto este número muda de tiro para tiro - um
       * cliente que o reconstruísse de uma constante desenharia sempre a mesma.
       */
      /* E a salva de uma ESTRUTURA diz de qual (gate V28H.X). */
      ...(projectile.fromSummon === undefined
        ? {}
        : { sv: projectile.fromSummon === 'leviathan' ? ('l' as const) : ('d' as const) }),
      ...pvpCardBlastFields(projectile.cardBlast),
    }));
    /*
     * Os mísseis, endereçados por ASSENTO como tudo o resto neste frame.
     *
     * Uma lista à parte de `proj`: um míssil não substitui um tiro nem se pendura
     * num, e a cena tem de os desenhar como duas coisas diferentes. `dx`/`dy` são
     * a direção autoritativa - um míssil curva, e o bico dele tem de apontar para
     * onde ele vai mesmo em vez de para onde dois quadros interpolados sugerem.
     */
    const mis = this.simulation.missilesInFlight().map((missile) => ({
      i: missile.id,
      x: missile.x,
      y: missile.y,
      dx: missile.dirX,
      dy: missile.dirY,
      s: pvpParticipantIdToDuelSeat(missile.ownerParticipantId),
    }));
    /*
     * Effects as TICKS REMAINING, sent rather than inferred.
     *
     * A client that counted its own timers down would disagree with the room the
     * moment a frame arrived late, and would keep showing an effect that had
     * already expired - which for Double Damage is the difference between a shot
     * the player expects to land for eighty and one that lands for forty.
     */
    /*
     * E os temporizadores de runa, pela MESMA regra dos cascos (gate V28H.L).
     *
     * Eles não levam posição, mas levam o assento - e mandar o estado de uma
     * nave que o adversário não pode ver é dizer-lhe que ela existe e o que ela
     * tem. Um casco omitido de `ships` é omitido daqui também, senão o corte
     * seria só metade de um corte.
     */
    const fxFor = (viewer: PvpTeam) =>
      this.simulation
        .participants()
        .filter((ship) => !this.simulation.isCloakedFrom(ship.participantId, viewer))
        .map((ship) => ({
          s: pvpParticipantIdToDuelSeat(ship.participantId),
          e: {
            dd: ship.effects.doubleDamageTicks,
            sp: ship.effects.speedTicks,
            tp: ship.effects.teleportCharges,
          },
        }));
    /*
     * The pads carry an INDEX and a kind, never a position.
     *
     * Where a pad is is a constant both sides already have, so sending it twenty
     * times a second would be paying for it twenty times a second - and it would
     * let the two disagree about where a rune is, which is the one thing a
     * shared constant cannot do.
     */
    /*
     * **As invocações** (gate V28H.L), na sua própria lista.
     *
     * Nem projéteis nem mísseis: uma invocação tem vida, e a cena desenha-lhe
     * uma barra. A vida viaja em basis points do máximo dela - a barra desenha
     * uma RAZÃO, e mandar dois números de quarenta dígitos para o cliente
     * calcular uma fracção seria pagar por uma divisão que a sala já sabe fazer.
     */
    /*
     * **Endereçadas por CADEIRA, como tudo o resto neste frame** (V28H.P).
     *
     * Isto estava errado desde o gate V28H.L: o dono de uma invocação viajava
     * como identidade de participante - `a1` - enquanto os cascos deste frame
     * viajam como cadeira - `a`. A cena procurava o dono na lista de cascos,
     * não o encontrava, e caía no tamanho mínimo: o drone existia, era público e
     * era desenhado **microscópico** num mundo de 24 milhões de unidades.
     *
     * "O drone não spawnou", disse o dono. Ele spawnava.
     */
    const sm = this.simulation.summonList().map((summon) => ({
      ...pvpSummonFrameOf(summon),
      p: pvpParticipantIdToDuelSeat(summon.ownerParticipantId),
      ...(summon.pilotParticipantId === null
        ? {}
        : { pl: pvpParticipantIdToDuelSeat(summon.pilotParticipantId) }),
    }));
    const runes = this.simulation.runeList().map((rune) => ({ p: rune.pad, k: rune.kind }));
    const left = Math.max(0, (PVP_TIME_LIMIT_TICKS - this.simulation.tick) * PVP_TICK_MS);
    /*
     * Os stacks de veneno ATIVOS, endereçados por ASSENTO como tudo o resto.
     *
     * Lidos e nunca esvaziados, ao contrário de `procs` e `blasts`: um stack não
     * é uma coisa que aconteceu, é uma coisa que existe, e esvaziá-lo apagaria a
     * marca do ecrã na publicação seguinte.
     *
     * Três campos - o que é, sobre quem, e quantas parcelas já pagou - e nada
     * mais: a autoria, o total congelado e o instante da aplicação são mecânica
     * que nenhuma cena lê.
     */
    const psn = this.simulation.poisonStacks().map((stack) => ({
      i: stack.id,
      q: pvpParticipantIdToDuelSeat(stack.targetParticipantId),
      n: stack.ticksDone,
    }));
    /*
     * As marcas de sangramento ATIVAS, endereçadas por ASSENTO (gate V28G.N).
     *
     * Lidas e nunca esvaziadas, como os stacks: uma marca não é uma coisa que
     * aconteceu, é uma coisa que existe.
     *
     * Três campos - o que é, de quem, e sobre quem. A AUTORIA viaja, ao contrário
     * da de um stack, porque é ela que decide quem pode detonar: é o que o botão
     * conta e é o que o efeito anima. A magnitude congelada e o instante da
     * aplicação continuam a ser mecânica que nenhuma cena lê.
     */
    const bld = this.simulation.bleedingMarks().map((mark) => ({
      i: mark.id,
      p: pvpParticipantIdToDuelSeat(mark.attackerParticipantId),
      q: pvpParticipantIdToDuelSeat(mark.targetParticipantId),
    }));
    /*
     * E as detonações, ESVAZIADAS aqui e uma vez só - os dois frames desta
     * publicação carregam a mesma lista, e a seguinte começa vazia.
     */
    const bdt = this.takeBleeds().map((burst) => ({
      p: pvpParticipantIdToDuelSeat(burst.p),
      /* O tique do EVENTO, copiado da caixa de saída. Nunca o da publicação. */
      t: burst.t,
      q: burst.q.map((target) => ({
        s: pvpParticipantIdToDuelSeat(target.s),
        n: target.n,
        /* O dano aplicado, em MILLI e como string decimal (gate V28G.S.1). */
        d: encodeHealthMilli(target.d),
      })),
    }));
    /*
     * Esvaziada AQUI, e uma vez so - os dois frames desta publicacao carregam a
     * mesma lista, e a publicacao seguinte comeca vazia. O duelo enderecca por
     * ASSENTO, que e o vocabulario que este frame ja usa para tudo o resto.
     */
    const procs = this.takeProcs().map((proc) => ({
      i: proc.i,
      e: proc.e,
      p: pvpParticipantIdToDuelSeat(proc.p),
      q: pvpParticipantIdToDuelSeat(proc.q),
      /*
       * O tique do EVENTO, copiado da caixa de saída.
       *
       * Nunca `this.simulation.tick`, que é o tique desta PUBLICAÇÃO: um frame
       * junta vários tiques, e trocá-los faria dois factos separados parecerem
       * simultâneos ao cliente que os desenha.
       */
      t: proc.t,
      /* A cura, em MILLI e como string decimal (gate V28G.S.1). */
      ...(proc.h === undefined ? {} : { h: encodeHealthMilli(proc.h) }),
      x: proc.x,
      y: proc.y,
    }));
    /* As explosões, endereçadas por ASSENTO como tudo o resto neste frame. */
    const blasts = this.takeBlasts().map((blast) => ({
      i: blast.i,
      e: blast.e,
      p: pvpParticipantIdToDuelSeat(blast.p),
      t: blast.t,
      x: blast.x,
      y: blast.y,
      r: blast.r,
      q: blast.q.map((id) => pvpParticipantIdToDuelSeat(id)),
    }));
    /* E as reflexões, pela mesma tradução exata (gate V28G.O). */
    const rfl = this.takeReflects().map((reflect) => ({
      e: reflect.e,
      p: pvpParticipantIdToDuelSeat(reflect.p),
      q: pvpParticipantIdToDuelSeat(reflect.q),
      t: reflect.t,
      k: reflect.k,
      s: reflect.s,
      x: reflect.x,
      y: reflect.y,
      tx: reflect.tx,
      ty: reflect.ty,
      /* O dano aplicado, em MILLI e como string decimal (gate V28G.S.1). */
      ...(reflect.d === undefined ? {} : { d: encodeHealthMilli(reflect.d) }),
    }));

    const frames: PvpOutboundFrame[] = [...this.connections.values()].map((connection) => ({
      connectionId: connection.connectionId,
      /*
       * `ack` is per PARTICIPANT, so each client reconciles against its own
       * inputs. Sending somebody else's sequence would make a client throw away
       * predictions it should have replayed.
       */
      frame: {
        t: 'state',
        v: PVP_DUEL_PROTOCOL_VERSION,
        k: this.simulation.tick,
        state: this.matchState,
        ack: this.simulation.shipOf(connection.participantId).appliedSeq,
        /*
         * Os três espaços DESTA ligação (gate V28H.J).
         *
         * Aqui e não em `ships` porque é privado: o `ack` acima já fazia deste
         * frame uma coisa construída por ligação, e um arrefecimento é
         * informação de decisão. O que é público - os canhões estão montados -
         * viaja em `ships[].sc`, porque os dois lados têm de os desenhar.
         */
        ck: pvpCardSlotsOf(this.simulation.cardSlotsOf(connection.participantId)),
        /* A lista DESTA ligação: um casco invisível não vem cá. */
        ships: shipsFor(this.simulation.shipOf(connection.participantId).team),
        proj,
        mis,
        sm,
        fx: fxFor(this.simulation.shipOf(connection.participantId).team),
        runes,
        procs,
        blasts,
        psn,
        bld,
        bdt,
        rfl,
        left,
      } as PvpServerFrame,
    }));
    return { frames, closes: [], finished: null, announcements: [] };
  }

  private welcomeFrame(participantId: PvpParticipantId): PvpOutboundPayload {
    if (this.ruleset !== PVP_DUEL_RULESET) {
      return this.teamWelcomeFrame(participantId);
    }
    const seat = pvpParticipantIdToDuelSeat(participantId);
    return {
      t: 'welcome',
      v: PVP_DUEL_PROTOCOL_VERSION,
      matchId: this.matchId,
      seat,
      state: this.matchState,
      mode: this.mode,
      rulesVersion: PVP_RULES_VERSION,
      // Where this participant's counter already stands. See the note on the field.
      ack: this.simulation.shipOf(participantId).appliedSeq,
      tickMs: PVP_TICK_MS,
      arena: {
        w: PVP_ARENA_WIDTH_MU,
        h: PVP_ARENA_HEIGHT_MU,
        obstacles: PVP_OBSTACLES.map((obstacle) => ({
          x: obstacle.x,
          y: obstacle.y,
          w: obstacle.w,
          h: obstacle.h,
        })),
      },
      appearance: {
        a: this.chairAppearance('a'),
        b: this.chairAppearance('b'),
      },
      /*
       * E o NOME de cada cadeira (gate V28H.R), ao lado da aparência e pela
       * mesma razão: ele é estático dentro de uma partida, e mandá-lo a cada
       * quadro seria pagar por uma coisa que nunca muda.
       */
      nick: {
        a: this.chairNickname('a'),
        b: this.chairNickname('b'),
      },
      /*
       * Both hulls, from the SIMULATION - the room's own state, never a
       * constant and never anything a client said. This is the whole of
       * protocol version 3 at the duel's end.
       */
      combatProfile: {
        a: this.shipProfile('a'),
        b: this.shipProfile('b'),
      },
      shipRadius: PVP_SHIP_RADIUS_MU,
      projectileRadius: PVP_PROJECTILE_RADIUS_MU,
      maxHealthMilli: Number(PVP_MAX_HEALTH_MILLI),
      timeLimitMs: PVP_TIME_LIMIT_TICKS * PVP_TICK_MS,
      reconnectGraceMs: PVP_RECONNECT_GRACE_MS,
    };
  }

  private chairAppearance(seat: PvpSeat): PvpAppearance | null {
    return this.chairs.get(pvpDuelSeatToParticipantId(seat))?.appearance ?? null;
  }

  /** O nome de uma cadeira, ou `null` enquanto ninguém apresentou bilhete. */
  private chairNickname(seat: PvpSeat): string | null {
    return this.chairs.get(pvpDuelSeatToParticipantId(seat))?.nickname ?? null;
  }

  /**
   * One seat's hull, as the wire describes it.
   *
   * Read from the simulation and copied, so a frame never shares an object with
   * a running match. There is no fallback: a duel always has both ships, and a
   * missing one would be a room that failed to build rather than a seat nobody
   * is in.
   */
  private shipProfile(seat: PvpSeat): PvpCombatProfileWire {
    return pvpCombatProfileToWire(this.simulation.shipOf(pvpDuelSeatToParticipantId(seat)).profile);
  }

  /* ------------------------------------------------------------------------ */
  /*  Persistence                                                             */
  /* ------------------------------------------------------------------------ */

  /**
   * Everything needed to rebuild this room later, and nothing else.
   *
   * Plain JSON: no `Map`, no `Set`, no class instance. Anything that needed a
   * constructor to come back would be something a storage layer could silently
   * fail to restore.
   *
   * The simulation snapshot already carries positions, velocities, health, aim,
   * cooldowns, applied sequences and projectiles - it is defined as "everything
   * that decides the future". What the ROOM adds is the part the simulation does
   * not know about: who holds each chair and under which generation, who has
   * declared ready, when each absent person dropped, and what has already been
   * announced.
   *
   * What is deliberately NOT in it: a socket. A socket cannot be serialised and
   * does not survive an eviction; what survives is the socket itself, held by
   * the runtime, carrying its own identity. The LOGICAL connection is here,
   * because it is a string the room minted, and {@link PvpRoom.reconcileConnections}
   * is how a rebuild finds out which of them are still real.
   */
  serialize(): PvpRoomStateV3 {
    return {
      v: PVP_ROOM_STATE_V3_VERSION,
      matchId: this.matchId,
      mode: this.mode,
      ruleset: this.ruleset,
      state: this.matchState,
      chairs: this.orderedChairs.map((chair) => this.serializeChair(chair)),
      /*
       * Always v3, for both rulesets. A duel used to write the historic two-seat
       * shape, which cannot hold a combat profile - so a duel that was told its
       * hulls differ lost the difference on the first eviction. `duel_v2` is
       * still READ, and upgraded explicitly; nothing writes it any more.
       */
      simulation:
        this.ruleset === PVP_DUEL_RULESET
          ? { kind: 'duel_v3', snapshot: this.simulation.snapshotV3() }
          : { kind: 'team_v3', snapshot: this.simulation.snapshotV3() },
      accumulatorMs: this.accumulatorMs,
      lastPumpAt: this.lastPumpAt,
      lastBroadcastAt: this.lastBroadcastAt,
      startAnnounced: this.startAnnounced,
      announced: this.announced,
    };
  }

  private serializeChair(chair: Chair): PvpRoomChairV3 {
    return {
      participantId: chair.participantId,
      team: chair.team,
      slot: chair.slot,
      claimed: chair.claimed,
      accountId: chair.accountId,
      generation: chair.generation,
      connectionId: chair.connectionId,
      ready: chair.ready,
      disconnectedSince: chair.disconnectedSince,
      /*
       * Rebuilt rather than adopted. A persisted room that shared a profile
       * object with the live one would let a later edit reach into storage, and
       * the whole point of freezing an appearance is that nothing can.
       */
      appearance: chair.appearance === null ? null : { ...chair.appearance },
      abandonAnnounced: chair.abandonAnnounced,
      eliminationAnnounced: chair.eliminationAnnounced,
      /*
       * Persisted, so a rebuilt room does not forget somebody walked out. A
       * Durable Object that resumed without this would hold a chair for a player
       * who had already abandoned, and the match would hang exactly as it did
       * before ADR 0043.
       */
      leftAt: chair.leftAt,
    };
  }

  /**
   * Rebuilds a room from storage, in either version.
   *
   * Takes `unknown` on purpose: this is what comes back out of a storage API,
   * and a reader typed to its own output has already assumed the thing it exists
   * to check. Refuses an unknown version rather than guessing at it - a match
   * resumed from a record this build does not understand is a different match,
   * and the player losing it would have no way to know.
   *
   * The rebuilt room's chairs hold the LOGICAL connections that were live when
   * it was written. The caller re-attaches the sockets the runtime still holds
   * and then calls {@link PvpRoom.reconcileConnections} with what it actually
   * has; anything absent is treated as a socket that just closed, which is what
   * it is.
   */
  static deserialize(value: unknown): PvpRoom {
    const state = pvpReadRoomState(value);
    const room = new PvpRoom(state.matchId, state.mode, {
      ruleset: state.ruleset,
      state: state.state,
      simulation: state.simulation,
    });
    room.accumulatorMs = state.accumulatorMs;
    room.lastPumpAt = state.lastPumpAt;
    room.lastBroadcastAt = state.lastBroadcastAt;
    room.startAnnounced = state.startAnnounced;
    room.announced = state.announced;
    for (const persisted of state.chairs) {
      const chair = room.requireChair(persisted.participantId);
      chair.claimed = persisted.claimed;
      chair.accountId = persisted.accountId;
      chair.generation = persisted.generation;
      chair.connectionId = persisted.connectionId;
      chair.ready = persisted.ready;
      chair.disconnectedSince = persisted.disconnectedSince;
      chair.appearance =
        persisted.appearance === null ? null : pvpReadAppearance(persisted.appearance);
      chair.abandonAnnounced = persisted.abandonAnnounced;
      chair.eliminationAnnounced = persisted.eliminationAnnounced;
      // Absent in rooms persisted before ADR 0043, which is exactly `null`.
      chair.leftAt = persisted.leftAt ?? null;
      if (persisted.connectionId !== null) {
        /*
         * The logical connection comes back with a FRESH rate-limit window.
         *
         * A rebuild is not evidence about anybody's behaviour, and carrying a
         * half-spent window across one would let an eviction close a socket
         * that had done nothing wrong. The window is one second; forgiving it
         * costs at most one second of allowance and cannot be provoked, because
         * a client cannot cause an eviction.
         */
        room.connections.set(persisted.connectionId, {
          connectionId: persisted.connectionId,
          participantId: persisted.participantId,
          generation: persisted.generation,
          windowStartedAt: 0,
          windowCount: 0,
          dropped: 0,
        });
      }
      /*
       * The snapshot may have been written while somebody was still attached, so
       * the simulation is told what the RECORD says rather than what the
       * snapshot happened to catch. A chair with no logical connection is a
       * chair nobody is flying.
       */
      room.simulation.setParticipantConnected(
        persisted.participantId,
        persisted.connectionId !== null,
      );
    }
    return room;
  }

  /* ------------------------------------------------------------------------ */
  /*  Internals                                                               */
  /* ------------------------------------------------------------------------ */

  private requireChair(participantId: PvpParticipantId): Chair {
    const chair = this.chairs.get(participantId);
    if (chair === undefined) {
      throw new PvpRoomError(
        'unknown_participant',
        `${this.ruleset} has no participant ${participantId}`,
      );
    }
    return chair;
  }

  private isOver(): boolean {
    return (
      this.matchState === 'completed' ||
      this.matchState === 'cancelled' ||
      this.matchState === 'expired'
    );
  }

  /** Drops a chair's current logical connection and asks for it to be closed. */
  private invalidateConnection(
    chair: Chair,
    reason: PvpCloseReason,
    keep?: string,
  ): PvpCloseInstruction[] {
    const previous = chair.connectionId;
    if (previous === null || previous === keep) {
      return [];
    }
    this.connections.delete(previous);
    chair.connectionId = null;
    return [{ connectionId: previous, reason }];
  }

  /* ------------------------------------------------------------------------ */
  /*  The 1x1 boundary: seat-shaped, and refusing rather than lying           */
  /* ------------------------------------------------------------------------ */

  /** How the duel ended, in the shape the wire and `pvp_matches` speak. */
  get result(): PvpResult | null {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    const result = this.simulation.teamResult;
    return result === null ? null : pvpTeamResultToDuelResult(result);
  }

  attach(
    seat: PvpSeat,
    connectionId: string,
    now: number,
    appearance?: PvpAppearance,
    accountId?: string,
  ): PvpRoomStep {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.attachParticipant(pvpDuelSeatToParticipantId(seat), connectionId, now, {
      ...(appearance === undefined ? {} : { appearance }),
      ...(accountId === undefined ? {} : { accountId }),
    });
  }

  reattach(
    seat: PvpSeat,
    connectionId: string,
    now: number,
    options: { readonly accountId?: string; readonly generation?: number } = {},
  ): boolean {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.reattachParticipant(pvpDuelSeatToParticipantId(seat), connectionId, now, options);
  }

  /** The welcome frame for a seat, so a reconnecting client can be re-briefed. */
  welcomeFor(seat: PvpSeat): PvpServerFrame {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    /*
     * Narrowed, not assumed: the line above refuses every ruleset but the duel,
     * so what `welcomeFrame` returns here is the duel protocol by construction.
     */
    return this.welcomeFrame(pvpDuelSeatToParticipantId(seat)) as PvpServerFrame;
  }

  /** One state frame for a seat, outside the broadcast cadence. */
  stateFrameFor(seat: PvpSeat): PvpServerFrame | null {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    const connectionId = this.requireChair(pvpDuelSeatToParticipantId(seat)).connectionId;
    if (connectionId === null) {
      return null;
    }
    const step = this.broadcastState();
    return (
      (step.frames.find((frame) => frame.connectionId === connectionId)?.frame as
        | PvpServerFrame
        | undefined) ?? null
    );
  }

  roomState(): PvpRoomState {
    pvpRequireDuelRuleset(this.ruleset, 'snapshot_not_supported_for_ruleset');
    return {
      matchId: this.matchId,
      mode: this.mode,
      state: this.matchState,
      snapshot: this.simulation.snapshot(),
    };
  }

  /**
   * Records that a seat now belongs to somebody, without saying who.
   *
   * The accept flow's historic call, kept because the two-seat product still
   * makes it. It marks the chair CLAIMED and leaves the account unrecorded,
   * which is exactly what it always meant - see {@link PvpRoom.claim} for the
   * version that names an occupant and can therefore refuse an impostor.
   */
  claimSeat(seat: PvpSeat): void {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    this.requireChair(pvpDuelSeatToParticipantId(seat)).claimed = true;
  }

  seatIsTaken(seat: PvpSeat): boolean {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.requireChair(pvpDuelSeatToParticipantId(seat)).claimed;
  }
}
